import { createClient } from '@supabase/supabase-js';
import { Database } from '../types';

// Check if authentication is enabled with better browser compatibility
const isAuthEnabled = (() => {
  try {
    return process.env.REACT_APP_AUTH_ENABLED !== 'false';
  } catch {
    // Fallback for browsers that don't support process.env properly (like Comet)
    return true;
  }
})();

// Web-compatible environment variables with fallbacks for different browsers
const supabaseUrl = (() => {
  try {
    return process.env.REACT_APP_SUPABASE_URL || 'https://supabase.stsc.ae';
  } catch {
    // Fallback for browsers that don't support process.env properly (like Comet)
    return 'https://supabase.stsc.ae';
  }
})();

const supabaseAnonKey = (() => {
  try {
    return process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3NjEyMTQxNDgsImV4cCI6MTg5MzQ1NjAwMCwiaXNzIjoiZG9rcGxveSJ9.S-au-8hTfcKVZuTnEY2j528qQs73nRYvKnWMIZw3Qts';
  } catch {
    // Fallback for browsers that don't support process.env properly (like Comet)
    return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3NjEyMTQxNDgsImV4cCI6MTg5MzQ1NjAwMCwiaXNzIjoiZG9rcGxveSJ9.S-au-8hTfcKVZuTnEY2j528qQs73nRYvKnWMIZw3Qts';
  }
})();

// Web-compatible AsyncStorage implementation with better browser support
const AsyncStorage = {
  getItem: async (key: string) => {
    try {
      // Check if localStorage is available (some browsers like Comet might not support it)
      if (typeof localStorage !== 'undefined' && localStorage) {
        return localStorage.getItem(key);
      }
      // Fallback to sessionStorage if localStorage isn't available
      if (typeof sessionStorage !== 'undefined' && sessionStorage) {
        return sessionStorage.getItem(key);
      }
      console.warn('No storage available in this browser');
      return null;
    } catch (error) {
      console.warn('Storage access failed:', error);
      return null;
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      // Try localStorage first
      if (typeof localStorage !== 'undefined' && localStorage) {
        localStorage.setItem(key, value);
        return;
      }
      // Fallback to sessionStorage
      if (typeof sessionStorage !== 'undefined' && sessionStorage) {
        sessionStorage.setItem(key, value);
        return;
      }
      console.warn('No storage available in this browser');
    } catch (error) {
      console.warn('Storage write failed:', error);
    }
  },
  removeItem: async (key: string) => {
    try {
      // Try localStorage first
      if (typeof localStorage !== 'undefined' && localStorage) {
        localStorage.removeItem(key);
        return;
      }
      // Fallback to sessionStorage
      if (typeof sessionStorage !== 'undefined' && sessionStorage) {
        sessionStorage.removeItem(key);
        return;
      }
    } catch (error) {
      console.warn('Storage removal failed:', error);
    }
  },
};

// Create the Supabase client
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: AsyncStorage,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
  },
});

// Test function to check if Supabase connection is working
export const testSupabaseConnection = async () => {
  // If auth is disabled, return success without testing database
  if (!isAuthEnabled) {
    return {
      success: true,
      message: 'Authentication disabled - database connection bypassed',
      authEnabled: false
    };
  }

  try {
    // Try to make a simple query
    const { data, error } = await supabase.from('vehicles').select('count');
    
    if (error) {
      console.error('Supabase connection test failed:', error.message);
      return {
        success: false,
        message: error.message,
        error,
        authEnabled: true
      };
    }
    
    console.log('Supabase connection test succeeded:', data);
    return {
      success: true,
      message: 'Connected to Supabase successfully',
      data,
      authEnabled: true
    };
  } catch (error) {
    console.error('Supabase connection test failed with exception:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error',
      error,
      authEnabled: true
    };
  }
};

// Vehicle API functions
export const getVehicles = async () => {
  try {
    const { data, error } = await supabase
      .from('vehicles')
      .select(`
        *,
        drivers:current_driver_id (
          id,
          name
        )
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data.map(vehicle => ({
      id: vehicle.id,
      licensePlate: vehicle.license_plate,
      makeModel: vehicle.make_model,
      year: vehicle.year,
      vinNumber: vehicle.vin, // Use 'vin' column
      image_url: vehicle.image_url,
      carStatus: vehicle.car_status,
      currentDriverId: vehicle.current_driver_id,
      currentDriverName: vehicle.drivers ? vehicle.drivers.name : null,
      dailyRent: vehicle.daily_rental_rate, // Use 'daily_rental_rate' column
      monthlyRentalRate: vehicle.monthly_rental_rate,
      notes: vehicle.notes,
      created_at: vehicle.created_at,
    }));
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    throw error;
  }
};

export const getVehicleById = async (id: string) => {
  const { data, error } = await supabase
    .from('vehicles')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  
  return {
    id: data.id,
    licensePlate: data.license_plate,
    makeModel: data.make_model,
    year: data.year,
    vinNumber: data.vin, // Use 'vin' column
    image_url: data.image_url,
    carStatus: data.car_status,
    currentDriverId: data.current_driver_id,
    dailyRent: data.daily_rental_rate, // Use 'daily_rental_rate' column
    notes: data.notes,
    created_at: data.created_at,
  };
};

export const createVehicle = async (vehicle: Omit<Database['public']['Tables']['vehicles']['Insert'], 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('vehicles')
    .insert({
      license_plate: vehicle.license_plate,
      make_model: vehicle.make_model,
      year: vehicle.year,
      vin: vehicle.vin || vehicle.vin_number, // Use 'vin' column not 'vin_number'
      image_url: vehicle.image_url,
      car_status: vehicle.car_status || 'available',
      current_driver_id: vehicle.current_driver_id,
      daily_rental_rate: vehicle.daily_rental_rate || vehicle.daily_rent, // Use 'daily_rental_rate' not 'daily_rent'
      notes: vehicle.notes,
    })
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

export const updateVehicle = async (id: string, vehicle: Partial<Database['public']['Tables']['vehicles']['Update']>) => {
  const { data, error } = await supabase
    .from('vehicles')
    .update(vehicle)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

export const deleteVehicle = async (id: string) => {
  // Get vehicle details before deletion for activity log
  const { data: vehicle } = await supabase
    .from('vehicles')
    .select('*')
    .eq('id', id)
    .single();
  
  const { error } = await supabase
    .from('vehicles')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
  
  // Log activity after successful deletion
  try {
    const { logActivity } = require('./activityLog');
    const vehicleInfo = vehicle 
      ? `${vehicle.license_plate} (${vehicle.make_model} ${vehicle.year || ''})`
      : `Vehicle ID: ${id}`;
    
    await logActivity({
      entity_type: 'vehicle',
      entity_id: id,
      action_type: 'delete',
      changes: {
        vehicle: { before: vehicleInfo, after: null },
        status: { before: vehicle?.car_status, after: 'deleted' },
      },
      description: `Deleted vehicle: ${vehicleInfo}`,
    });
    console.log('✅ Vehicle deletion logged to activity log');
  } catch (logError) {
    console.error('⚠️ Failed to log vehicle deletion:', logError);
    // Don't fail the deletion if logging fails
  }
  
  return true;
};

// Driver API functions
export const getDrivers = async () => {
  try {
    const { data, error } = await supabase
      .from('drivers')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data.map(driver => ({
      id: driver.id,
      name: driver.name,
      licenseNumber: driver.license_number,
      licenseExpiry: driver.license_expiry_date, // ✅ FIXED: use license_expiry_date from database
      phone: driver.phone,
      image_url: driver.image_url,
      status: driver.status,
      currentVehicleId: driver.current_vehicle_id,
      driver_id: driver.driver_id,
      careem_captain_id: driver.careem_captain_id,
      dailyTarget: driver.daily_target,
      deposit: driver.deposit,
      nationality: driver.nationality,
      notes: driver.notes,
      created_at: driver.created_at,
    }));
  } catch (error) {
    console.error('Error fetching drivers:', error);
    throw error;
  }
};

export const getDriverById = async (id: string) => {
  const { data, error } = await supabase
    .from('drivers')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  
  return {
    id: data.id,
    name: data.name,
    licenseNumber: data.license_number,
    licenseExpiry: data.license_expiry,
    phone: data.phone,
    image_url: data.image_url,
    status: data.status,
    currentVehicleId: data.current_vehicle_id, // ✅ FIXED: use current_vehicle_id from migration
    dailyTarget: data.daily_target,
    deposit: data.deposit,
    nationality: data.nationality,
    notes: data.notes,
    created_at: data.created_at,
  };
};

export const createDriver = async (driver: Omit<Database['public']['Tables']['drivers']['Insert'], 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('drivers')
    .insert({
      name: driver.name,
      license_number: driver.license_number,
      license_expiry_date: driver.license_expiry || driver.license_expiry_date, // ✅ FIXED: Use correct column name
      phone: driver.phone,
      image_url: driver.image_url,
      status: driver.status || 'active',
      current_vehicle_id: driver.current_vehicle_id,
      daily_target: driver.daily_target,
      deposit: driver.deposit,
      nationality: driver.nationality,
      notes: driver.notes,
      driver_id: driver.driver_id,
      careem_captain_id: driver.careem_captain_id,
    })
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

export const updateDriver = async (id: string, driver: Partial<Database['public']['Tables']['drivers']['Update']>) => {
  const { data, error } = await supabase
    .from('drivers')
    .update(driver)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

export const deleteDriver = async (id: string) => {
  // Get driver details before deletion for activity log
  const { data: driver } = await supabase
    .from('drivers')
    .select('*')
    .eq('id', id)
    .single();
  
  const { error } = await supabase
    .from('drivers')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
  
  // Log activity after successful deletion
  try {
    const { logActivity } = require('./activityLog');
    const driverInfo = driver ? driver.name : `Driver ID: ${id}`;
    
    await logActivity({
      entity_type: 'driver',
      entity_id: id,
      action_type: 'delete',
      changes: {
        driver: { before: driverInfo, after: null },
        phone: { before: driver?.phone, after: null },
        status: { before: driver?.status, after: 'deleted' },
      },
      description: `Deleted driver: ${driverInfo}`,
    });
    console.log('✅ Driver deletion logged to activity log');
  } catch (logError) {
    console.error('⚠️ Failed to log driver deletion:', logError);
    // Don't fail the deletion if logging fails
  }
  
  return true;
};

// Job Card API functions
export const getJobCards = async () => {
  try {
    const { data, error } = await supabase
      .from('job_cards')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
  
  return data.map(jobCard => ({
    id: jobCard.id,
    jobNumber: jobCard.job_number || '',
    licensePlate: jobCard.license_plate || '',
    makeModel: jobCard.make_model || '',
    year: jobCard.year || '',
    vin: jobCard.vin || '',
    mileage: jobCard.mileage || '',
    requestDate: jobCard.request_date || '',
    requiredBy: jobCard.required_by || '',
    requestedBy: jobCard.requested_by || '',
    jobDescription: jobCard.job_description || '',
    workDescription: jobCard.work_description || '',
    issues: jobCard.issues || '',
    notes: jobCard.notes || '',
    jobStatus: jobCard.job_status || 'pending',
    vehicleId: jobCard.vehicle_id,
    // Work types - NEW CATEGORIES
    underMaintenance: jobCard.under_maintenance || false,
    accidentRepair: jobCard.accident_repair || false,
    underMaintenanceAlQouz: jobCard.under_maintenance_al_qouz || false,
    underMaintenanceAgency: jobCard.under_maintenance_agency || false,
    others: jobCard.others || false,
    // OLD work types (deprecated, kept for backwards compatibility)
    maintenance: jobCard.maintenance || false,
    repair: jobCard.repair || false,
    inspection: jobCard.inspection || false,
    diagnostic: jobCard.diagnostic || false,
    accident: jobCard.accident || false,
    other: jobCard.other || false,
    // Vehicle condition checks
    lightsOk: jobCard.lights_ok,
    wipersOk: jobCard.wipers_ok,
    hornOk: jobCard.horn_ok,
    acOk: jobCard.ac_ok,
    tiresOk: jobCard.tires_ok,
    mirrorsOk: jobCard.mirrors_ok,
    // Additional fields
    driver: jobCard.driver,
    keys: jobCard.keys,
    fuelLevel: jobCard.fuel_level,
    handoverDate: jobCard.handover_date,
    returnDate: jobCard.return_date,
    handedBy: jobCard.handed_by,
    receivedBy: jobCard.received_by,
    completedBy: jobCard.completed_by,
    created_at: jobCard.created_at,
    // Signatures
    maintenanceSignatureSvg: jobCard.maintenance_signature_svg,
    workshopSignatureSvg: jobCard.workshop_signature_svg,
    // Vehicle damages
    vehicleDamages: jobCard.vehicle_damages || [],
  }));
  } catch (error) {
    console.error('Error fetching job cards:', error);
    throw error;
  }
};

export const getJobCardById = async (id: string) => {
  const { data, error } = await supabase
    .from('job_cards')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  
  return {
    id: data.id,
    jobNumber: data.job_number || '',
    licensePlate: data.license_plate || '',
    makeModel: data.make_model || '',
    year: data.year || '',
    vin: data.vin || '',
    mileage: data.mileage || '',
    requestDate: data.request_date || '',
    requiredBy: data.required_by || '',
    requestedBy: data.requested_by || '',
    jobDescription: data.job_description || '',
    workDescription: data.work_description || '',
    issues: data.issues || '',
    notes: data.notes || '',
    jobStatus: data.job_status || 'pending',
    vehicleId: data.vehicle_id,
    // Work types
    maintenance: data.maintenance || false,
    repair: data.repair || false,
    inspection: data.inspection || false,
    diagnostic: data.diagnostic || false,
    accident: data.accident || false,
    other: data.other || false,
    // Vehicle condition checks
    lightsOk: data.lights_ok,
    wipersOk: data.wipers_ok,
    hornOk: data.horn_ok,
    acOk: data.ac_ok,
    tiresOk: data.tires_ok,
    mirrorsOk: data.mirrors_ok,
    // Additional fields
    driver: data.driver,
    keys: data.keys,
    fuelLevel: data.fuel_level,
    handoverDate: data.handover_date,
    returnDate: data.return_date,
    handedBy: data.handed_by,
    receivedBy: data.received_by,
    completedBy: data.completed_by,
    created_at: data.created_at,
    // Signatures
    maintenanceSignatureSvg: data.maintenance_signature_svg,
    workshopSignatureSvg: data.workshop_signature_svg,
    // Vehicle damages
    vehicleDamages: data.vehicle_damages || [],
  };
};

export const createJobCard = async (jobCard: any) => {
  const { data, error } = await supabase
    .from('job_cards')
    .insert({
      job_number: jobCard.jobNumber,
      license_plate: jobCard.licensePlate,
      make_model: jobCard.makeModel,
      year: jobCard.year,
      vin: jobCard.vin,
      mileage: jobCard.mileage,
      request_date: jobCard.requestDate,
      required_by: jobCard.requiredBy,
      requested_by: jobCard.requestedBy,
      job_description: jobCard.jobDescription,
      work_description: jobCard.workDescription,
      notes: jobCard.notes,
      job_status: jobCard.jobStatus || 'pending',
      vehicle_id: jobCard.vehicleId,
      // Work types - NEW CATEGORIES
      under_maintenance: jobCard.underMaintenance || false,
      accident_repair: jobCard.accidentRepair || false,
      under_maintenance_al_qouz: jobCard.underMaintenanceAlQouz || false,
      under_maintenance_agency: jobCard.underMaintenanceAgency || false,
      others: jobCard.others || false,
      // OLD work types (deprecated, kept for backwards compatibility)
      maintenance: jobCard.maintenance || false,
      repair: jobCard.repair || false,
      inspection: jobCard.inspection || false,
      diagnostic: jobCard.diagnostic || false,
      accident: jobCard.accident || false,
      other: jobCard.other || false,
    })
    .select()
    .single();
  
  if (error) throw error;
  
  // Log job card creation activity
  try {
    // Get current user from authStore
    const { useAuthStore } = await import('../stores/authStore');
    const currentUser = useAuthStore.getState().user;
    
    await supabase.from('activity_log').insert({
      entity_type: 'job_card',
      entity_id: String(data.id),
      action_type: 'create',
      description: `Created job card ${data.job_number} for ${data.license_plate}`,
      changes: {
        job_number: data.job_number,
        license_plate: data.license_plate,
        vehicle_id: data.vehicle_id,
        job_status: data.job_status,
        job_description: data.job_description,
      },
      user_id: currentUser?.id || null,
      user_name: currentUser?.name || currentUser?.email || 'System',
      user_email: currentUser?.email || null,
      user_role: currentUser?.role || null,
    });
  } catch (logError) {
    // Don't fail the operation if activity logging fails
    console.warn('Failed to log job card creation activity:', logError);
  }
  
  return data;
};

export const updateJobCard = async (id: string, jobCard: Partial<Database['public']['Tables']['job_cards']['Update']>) => {
  const { data, error } = await supabase
    .from('job_cards')
    .update(jobCard)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

export const deleteJobCard = async (id: string) => {
  // Get job card details before deletion for activity log
  const { data: jobCard } = await supabase
    .from('job_cards')
    .select('*')
    .eq('id', id)
    .single();
  
  const { error } = await supabase
    .from('job_cards')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
  
  // Log activity after successful deletion
  try {
    const { logActivity } = require('./activityLog');
    const jobCardInfo = jobCard 
      ? `Job #${jobCard.job_number} - ${jobCard.license_plate} (${jobCard.make_model})`
      : `Job Card ID: ${id}`;
    
    await logActivity({
      entity_type: 'job_card',
      entity_id: id,
      action_type: 'delete',
      changes: {
        job_card: { before: jobCardInfo, after: null },
        status: { before: jobCard?.job_status, after: 'deleted' },
        license_plate: { before: jobCard?.license_plate, after: null },
      },
      description: `Deleted job card: ${jobCardInfo}`,
    });
    console.log('✅ Job card deletion logged to activity log');
  } catch (logError) {
    console.error('⚠️ Failed to log job card deletion:', logError);
    // Don't fail the deletion if logging fails
  }
  
  return true;
};

// Vehicle Assignment API functions
export const getVehicleAssignments = async () => {
  const { data, error } = await supabase
    .from('assignment_history')
    .select(`
      *,
      vehicles:vehicle_id (license_plate, make_model),
      drivers:primary_driver_id (name, phone)
    `)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  
  return (data || []).map(assignment => ({
    id: assignment.id,
    vehicleId: assignment.vehicle_id,
    driverId: assignment.primary_driver_id,
    startDate: assignment.assignment_date,
    endDate: assignment.return_date,
    dailyRent: 0, // Not stored in assignment_history, calculated from vehicle
    assignmentStatus: assignment.status,
    notes: assignment.notes,
    created_at: assignment.created_at,
    // Include related data for display
    vehicle: assignment.vehicles,
    driver: assignment.drivers,
  }));
};

export const createVehicleAssignment = async (assignment: any) => {
  const { data, error } = await supabase
    .from('assignment_history')
    .insert({
      vehicle_id: assignment.vehicle_id,
      primary_driver_id: assignment.driver_id,
      assignment_date: assignment.start_date || new Date().toISOString().split('T')[0],
      return_date: assignment.end_date,
      status: assignment.assignment_status || 'active',
      notes: assignment.notes,
      is_shared: false,
    })
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

export const updateVehicleAssignment = async (id: string, updates: any) => {
  const mappedUpdates: any = {};
  
  // Map old field names to new schema
  if (updates.driver_id) mappedUpdates.primary_driver_id = updates.driver_id;
  if (updates.start_date) mappedUpdates.assignment_date = updates.start_date;
  if (updates.end_date) mappedUpdates.return_date = updates.end_date;
  if (updates.assignment_status) mappedUpdates.status = updates.assignment_status;
  if (updates.vehicle_id) mappedUpdates.vehicle_id = updates.vehicle_id;
  if (updates.notes) mappedUpdates.notes = updates.notes;
  
  const { data, error } = await supabase
    .from('assignment_history')
    .update(mappedUpdates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

export const deleteVehicleAssignment = async (id: string) => {
  const { error } = await supabase
    .from('assignment_history')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// Transaction API functions
export const getTransactions = async () => {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  
  return data.map(transaction => ({
    id: transaction.id,
    driverId: transaction.driver_id,
    vehicleId: transaction.vehicle_id,
    amount: transaction.amount,
    transactionType: transaction.transaction_type,
    description: transaction.description,
    paymentMethod: transaction.payment_method,
    transactionDate: transaction.transaction_date,
    includeVAT: transaction.include_vat,
    vatAmount: transaction.vat_amount,
    created_at: transaction.created_at,
  }));
};

export const createTransaction = async (transaction: Omit<Database['public']['Tables']['transactions']['Insert'], 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('transactions')
    .insert({
      driver_id: transaction.driver_id,
      vehicle_id: transaction.vehicle_id,
      amount: transaction.amount,
      transaction_type: transaction.transaction_type,
      description: transaction.description,
      payment_method: transaction.payment_method,
      transaction_date: transaction.transaction_date,
      include_vat: transaction.include_vat,
      vat_amount: transaction.vat_amount,
    })
    .select()
    .single();
  
  if (error) throw error;
  
  return data;
};

// Assignment History API functions
export const getAssignmentHistory = async () => {
  console.log('🔍 Fetching assignment history with joins...');
  
  const { data, error } = await supabase
    .from('assignment_history')
    .select(`
      *,
      vehicles!vehicle_id (license_plate, make_model, year, color),
      primaryDriver:drivers!primary_driver_id (name, phone),
      secondaryDriver:drivers!secondary_driver_id (name, phone)
    `)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('❌ Error fetching assignment history:', error);
    throw error;
  }
  
  console.log(`📊 Raw assignment data from database: ${data?.length || 0} records`);
  
  // Log details about assignments with secondary drivers
  const assignmentsWithSecondary = data?.filter(a => a.secondary_driver_id) || [];
  console.log(`👥 Found ${assignmentsWithSecondary.length} assignments with secondary drivers`);
  
  assignmentsWithSecondary.slice(0, 3).forEach((assignment, index) => {
    console.log(`Assignment ${index + 1}:`, {
      id: assignment.id,
      vehicle: assignment.vehicles?.license_plate,
      primary_driver: assignment.primaryDriver?.name,
      secondary_driver: assignment.secondaryDriver?.name,
      secondary_driver_id: assignment.secondary_driver_id,
      is_shared: assignment.is_shared,
      created_at: assignment.created_at
    });
  });
  
  const mapped = (data || []).map(assignment => ({
    id: assignment.id,
    vehicle_id: assignment.vehicle_id,
    primary_driver_id: assignment.primary_driver_id,
    secondary_driver_id: assignment.secondary_driver_id,
    assignment_date: assignment.assignment_date,
    return_date: assignment.return_date,
    daily_rate: assignment.daily_rate,
    monthly_rent_rate: assignment.monthly_rent_rate, // ✅ FIXED: Include monthly_rent_rate
    total_days: assignment.total_days,
    total_amount: assignment.total_amount,
    is_shared: assignment.is_shared,
    assignment_type: assignment.assignment_type,
    status: assignment.status,
    delivery_photos: assignment.delivery_photos || [], // ✅ NEW: Include delivery photos
    receiving_photos: assignment.receiving_photos || [], // ✅ NEW: Include receiving photos
    created_at: assignment.created_at,
    updated_at: assignment.updated_at,
    // Include related data for display
    vehicle: assignment.vehicles,
    primaryDriver: assignment.primaryDriver,
    secondaryDriver: assignment.secondaryDriver,
  }));
  
  console.log(`✅ Mapped ${mapped.length} assignments for return`);
  return mapped;
};

export const createAssignmentHistory = async (assignment: any) => {
  console.log('🔥 Creating assignment in database:', {
    vehicle_id: assignment.vehicle_id,
    primary_driver_id: assignment.primary_driver_id,
    secondary_driver_id: assignment.secondary_driver_id,
    is_shared: assignment.is_shared,
    status: assignment.status
  });
  
  try {
    // STEP 1: Mark any existing active assignments for this vehicle+driver as completed
    console.log('🔄 Checking for existing active assignments to complete...');
    const { data: existingAssignments, error: checkError } = await supabase
      .from('assignment_history')
      .select('id')
      .eq('vehicle_id', assignment.vehicle_id)
      .eq('primary_driver_id', assignment.primary_driver_id)
      .eq('status', 'active');

    if (checkError) {
      console.warn('⚠️ Error checking existing assignments:', checkError);
    } else if (existingAssignments && existingAssignments.length > 0) {
      console.log(`📌 Found ${existingAssignments.length} existing active assignment(s) - marking as completed`);
      
      const { error: updateError } = await supabase
        .from('assignment_history')
        .update({ 
          status: 'completed',
          return_date: new Date().toISOString()
        })
        .eq('vehicle_id', assignment.vehicle_id)
        .eq('primary_driver_id', assignment.primary_driver_id)
        .eq('status', 'active');

      if (updateError) {
        console.error('❌ Error completing old assignments:', updateError);
      } else {
        console.log('✅ Old assignments marked as completed');
      }
    } else {
      console.log('✅ No existing active assignments found');
    }

    // STEP 2: Create the new assignment
    const { data, error } = await supabase
      .from('assignment_history')
      .insert({
        vehicle_id: assignment.vehicle_id,
        primary_driver_id: assignment.primary_driver_id,
        secondary_driver_id: assignment.secondary_driver_id,
        assignment_date: assignment.assignment_date,
        return_date: assignment.return_date,
        daily_rate: assignment.daily_rate || 0,
        monthly_rent_rate: assignment.monthly_rent_rate,
        total_days: assignment.total_days,
        total_amount: assignment.total_amount,
        is_shared: assignment.is_shared || false,
        assignment_type: assignment.assignment_type || 'rental',
        status: assignment.status || 'active',
        delivery_photos: assignment.delivery_photos || [],
        receiving_photos: assignment.receiving_photos || [],
      })
      .select()
      .single();

    if (error) throw error;
    
    // Log assignment creation activity with rich context
    try {
      const { logActivity } = require('./activityLog');
      await logActivity({
        entity_type: 'assignment',
        entity_id: data.id,
        action_type: 'create',
        changes: {
          after: {
            vehicle_id: data.vehicle_id,
            primary_driver_id: data.primary_driver_id,
            secondary_driver_id: data.secondary_driver_id,
            assignment_date: data.assignment_date,
            status: data.status,
            is_shared: data.is_shared,
          },
        },
        description: `Created assignment for vehicle ${assignment.vehicle_id}`,
      });
      console.log('✅ Assignment creation logged');
    } catch (logError) {
      console.warn('⚠️ Failed to log assignment creation:', logError);
      // Don't throw - activity logging is optional
    }

    return {
      id: data.id,
      vehicle_id: data.vehicle_id,
      primary_driver_id: data.primary_driver_id,
      secondary_driver_id: data.secondary_driver_id,
      assignment_date: data.assignment_date,
      return_date: data.return_date,
      daily_rate: data.daily_rate,
      monthly_rent_rate: data.monthly_rent_rate,
      total_days: data.total_days,
      total_amount: data.total_amount,
      is_shared: data.is_shared,
      assignment_type: data.assignment_type,
      status: data.status,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  } catch (error) {
    console.error('❌ Error creating assignment:', error);
    throw error;
  }
};

export const updateAssignmentHistory = async (id: string, updates: any) => {
  console.log('📝 Updating assignment:', id, updates);
  
  // STEP 1: Get current assignment data with vehicle and driver details for activity logging
  const { data: currentData, error: fetchError } = await supabase
    .from('assignment_history')
    .select(`
      *,
      vehicle:vehicles!assignment_history_vehicle_id_fkey(license_plate, make_model, year),
      primaryDriver:drivers!assignment_history_primary_driver_id_fkey(name),
      secondaryDriver:drivers!assignment_history_secondary_driver_id_fkey(name)
    `)
    .eq('id', id)
    .single();
  
  if (fetchError) {
    console.error('❌ Error fetching current assignment for update:', fetchError);
    throw fetchError;
  }
  
  // STEP 2: Convert updates to match database schema
  const dbUpdates: any = {};
  if (updates.vehicle_id) dbUpdates.vehicle_id = updates.vehicle_id;
  if (updates.primary_driver_id) dbUpdates.primary_driver_id = updates.primary_driver_id;
  if (updates.secondary_driver_id !== undefined) dbUpdates.secondary_driver_id = updates.secondary_driver_id;
  if (updates.assignment_date) dbUpdates.assignment_date = updates.assignment_date;
  if (updates.return_date !== undefined) dbUpdates.return_date = updates.return_date;
  if (updates.daily_rate !== undefined) dbUpdates.daily_rate = updates.daily_rate;
  if (updates.monthly_rent_rate !== undefined) dbUpdates.monthly_rent_rate = updates.monthly_rent_rate;
  if (updates.total_days !== undefined) dbUpdates.total_days = updates.total_days;
  if (updates.total_amount !== undefined) dbUpdates.total_amount = updates.total_amount;
  if (updates.is_shared !== undefined) dbUpdates.is_shared = updates.is_shared;
  if (updates.assignment_type) dbUpdates.assignment_type = updates.assignment_type;
  if (updates.status) dbUpdates.status = updates.status;
  
  // Always update the updated_at timestamp
  dbUpdates.updated_at = new Date().toISOString();

  // STEP 3: Update the assignment
  const { data, error } = await supabase
    .from('assignment_history')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('❌ Error updating assignment:', error);
    throw error;
  }
  
  // STEP 4: Log activity with rich context and before/after values
  try {
    const { logActivity } = require('./activityLog');
    
    // Build context information
    const vehicleInfo = currentData.vehicle 
      ? `${currentData.vehicle.license_plate} (${currentData.vehicle.make_model})`
      : `Vehicle ID: ${currentData.vehicle_id}`;
    const driverInfo = currentData.primaryDriver?.name || `Driver ID: ${currentData.primary_driver_id}`;
    
    // Build changes object with human-readable field names
    const changes: Record<string, { before: any; after: any }> = {};
    const changedFields: string[] = [];
    
    if (dbUpdates.assignment_date && currentData.assignment_date !== dbUpdates.assignment_date) {
      changes['Assignment Date'] = { before: currentData.assignment_date, after: dbUpdates.assignment_date };
      changedFields.push('assignment date');
    }
    if (dbUpdates.return_date !== undefined && currentData.return_date !== dbUpdates.return_date) {
      changes['Return Date'] = { before: currentData.return_date || 'None', after: dbUpdates.return_date || 'None' };
      changedFields.push('return date');
    }
    if (dbUpdates.monthly_rent_rate !== undefined && currentData.monthly_rent_rate !== dbUpdates.monthly_rent_rate) {
      changes['Monthly Rent'] = { before: `AED ${currentData.monthly_rent_rate || 0}`, after: `AED ${dbUpdates.monthly_rent_rate}` };
      changedFields.push('monthly rent');
    }
    if (dbUpdates.daily_rate !== undefined && currentData.daily_rate !== dbUpdates.daily_rate) {
      changes['Daily Rate'] = { before: `AED ${currentData.daily_rate || 0}`, after: `AED ${dbUpdates.daily_rate}` };
      changedFields.push('daily rate');
    }
    if (dbUpdates.status && currentData.status !== dbUpdates.status) {
      changes['Status'] = { before: currentData.status, after: dbUpdates.status };
      changedFields.push('status');
    }
    if (dbUpdates.secondary_driver_id !== undefined && currentData.secondary_driver_id !== dbUpdates.secondary_driver_id) {
      const beforeDriver = currentData.secondaryDriver?.name || (currentData.secondary_driver_id ? 'Unknown Driver' : 'None');
      const afterDriver = dbUpdates.secondary_driver_id ? 'Driver Updated' : 'Removed';
      changes['Secondary Driver'] = { before: beforeDriver, after: afterDriver };
      changedFields.push('secondary driver');
    }
    
    if (Object.keys(changes).length > 0) {
      await logActivity({
        entity_type: 'assignment',
        entity_id: id,
        action_type: 'update',
        changes,
        description: `Updated assignment: ${vehicleInfo} - ${driverInfo} (changed: ${changedFields.join(', ')})`,
      });
      console.log('✅ Assignment update logged to activity log');
    }
  } catch (logError) {
    console.error('⚠️ Failed to log assignment update:', logError);
    // Don't fail the update if logging fails
  }
  
  console.log('✅ Assignment updated successfully');
  
  return {
    id: data.id,
    vehicle_id: data.vehicle_id,
    primary_driver_id: data.primary_driver_id,
    secondary_driver_id: data.secondary_driver_id,
    assignment_date: data.assignment_date,
    return_date: data.return_date,
    daily_rate: data.daily_rate,
    monthly_rent_rate: data.monthly_rent_rate,
    total_days: data.total_days,
    total_amount: data.total_amount,
    is_shared: data.is_shared,
    assignment_type: data.assignment_type,
    status: data.status,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
};

export const deleteAssignmentHistory = async (id: string) => {
  console.log('🗑️ Deleting assignment:', id);
  
  // STEP 1: Get full assignment data with vehicle and driver details before deletion
  const { data: assignmentData, error: fetchError } = await supabase
    .from('assignment_history')
    .select(`
      *,
      vehicle:vehicles!assignment_history_vehicle_id_fkey(license_plate, make_model, year),
      primaryDriver:drivers!assignment_history_primary_driver_id_fkey(name, phone),
      secondaryDriver:drivers!assignment_history_secondary_driver_id_fkey(name, phone)
    `)
    .eq('id', id)
    .single();
  
  if (fetchError) {
    console.error('❌ Error fetching assignment for deletion:', fetchError);
    throw fetchError;
  }
  
  // STEP 2: Delete related records first (cascade delete)
  console.log('🗑️ Deleting related delivery and receiving forms...');
  
  // Get delivery form IDs for this assignment
  const { data: deliveryForms } = await supabase
    .from('vehicle_delivery_forms')
    .select('id')
    .eq('assignment_id', id);
  
  // Get receiving form IDs for this assignment
  const { data: receivingForms } = await supabase
    .from('vehicle_receiving_forms')
    .select('id')
    .eq('assignment_id', id);
  
  // Delete damages associated with delivery forms
  if (deliveryForms && deliveryForms.length > 0) {
    const deliveryFormIds = deliveryForms.map(f => f.id);
    console.log('🗑️ Deleting vehicle damages for delivery forms:', deliveryFormIds);
    
    const { error: damagesError } = await supabase
      .from('vehicle_damages')
      .delete()
      .in('delivery_form_id', deliveryFormIds);
    
    if (damagesError) {
      console.error('⚠️ Error deleting delivery form damages:', damagesError);
    }
  }
  
  // Delete damages associated with receiving forms
  if (receivingForms && receivingForms.length > 0) {
    const receivingFormIds = receivingForms.map(f => f.id);
    console.log('🗑️ Deleting vehicle damages for receiving forms:', receivingFormIds);
    
    const { error: receivingDamagesError } = await supabase
      .from('vehicle_damages')
      .delete()
      .in('receiving_form_id', receivingFormIds);
    
    if (receivingDamagesError) {
      console.error('⚠️ Error deleting receiving form damages:', receivingDamagesError);
    }
  }
  
  // Delete delivery forms
  if (deliveryForms && deliveryForms.length > 0) {
    console.log('🗑️ Deleting delivery forms...');
    const { error: deliveryError } = await supabase
      .from('vehicle_delivery_forms')
      .delete()
      .eq('assignment_id', id);
    
    if (deliveryError) {
      console.error('⚠️ Error deleting delivery forms:', deliveryError);
    }
  }
  
  // Delete receiving forms
  if (receivingForms && receivingForms.length > 0) {
    console.log('🗑️ Deleting receiving forms...');
    const { error: receivingError } = await supabase
      .from('vehicle_receiving_forms')
      .delete()
      .eq('assignment_id', id);
    
    if (receivingError) {
      console.error('⚠️ Error deleting receiving forms:', receivingError);
    }
  }
  
  // STEP 3: Delete the assignment itself
  console.log('🗑️ Deleting assignment record...');
  const { error } = await supabase
    .from('assignment_history')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('❌ Error deleting assignment:', error);
    throw error;
  }
  
  // STEP 4: Log deletion activity with rich context
  try {
    const { logActivity } = require('./activityLog');
    
    // Build descriptive message with vehicle and driver details
    const vehicleInfo = assignmentData.vehicle 
      ? `${assignmentData.vehicle.license_plate} (${assignmentData.vehicle.make_model} ${assignmentData.vehicle.year || ''})`
      : `Vehicle ID: ${assignmentData.vehicle_id}`;
    
    const driverInfo = assignmentData.primaryDriver?.name || `Driver ID: ${assignmentData.primary_driver_id}`;
    const rentInfo = assignmentData.monthly_rent_rate 
      ? `AED ${assignmentData.monthly_rent_rate}/month`
      : assignmentData.daily_rate 
      ? `AED ${assignmentData.daily_rate}/day`
      : 'No rent set';
    
    const secondaryInfo = assignmentData.secondaryDriver 
      ? ` + ${assignmentData.secondaryDriver.name} (secondary)`
      : '';
    
    await logActivity({
      entity_type: 'assignment',
      entity_id: id,
      action_type: 'delete',
      changes: {
        vehicle: { before: vehicleInfo, after: null },
        driver: { before: driverInfo + secondaryInfo, after: null },
        rent: { before: rentInfo, after: null },
        status: { before: assignmentData.status, after: 'deleted' },
        assignment_date: { before: assignmentData.assignment_date, after: null },
      },
      description: `Deleted assignment: ${vehicleInfo} - ${driverInfo}${secondaryInfo} (${rentInfo}, was ${assignmentData.status})`,
    });
    console.log('✅ Assignment deletion logged to activity log');
  } catch (logError) {
    console.error('⚠️ Failed to log assignment deletion:', logError);
    // Don't fail the deletion if logging fails
  }
  
  console.log('✅ Assignment deleted successfully');
};

// Vehicle Receiving Forms API
export const createVehicleReceivingForm = async (formData: any) => {
  try {
    const { logActivity } = require('./activityLog');
    const dbData = {
      reference_number: formData.reference_number || formData.referenceNumber,
      assignment_id: formData.assignment_id || formData.assignmentId,
      vehicle_id: formData.vehicle_id || formData.vehicleId,
      driver_id: formData.driver_id || formData.driverId,
      secondary_driver_id: formData.secondary_driver_id || formData.secondaryDriverId || null,
      assignment_date: formData.assignment_date || formData.assignmentDate,
      assignment_time: formData.assignment_time || formData.assignmentTime,
      rent_amount: formData.rent_amount !== undefined ? formData.rent_amount : (formData.rentAmount !== undefined ? formData.rentAmount : 0),
      current_mileage: formData.current_mileage || formData.currentMileage || 0,
      previous_mileage: formData.previous_mileage || formData.previousMileage || null,
      exterior_condition: formData.exterior_condition || formData.exteriorCondition,
      interior_condition: formData.interior_condition || formData.interiorCondition,
      tire_condition: formData.tire_condition || formData.tireCondition || null,
      has_damages: formData.has_damages !== undefined ? formData.has_damages : (formData.hasDamages || false),
      existing_damage_description: formData.existing_damage_description || formData.existingDamageDescription || null,
      has_spare_key: formData.has_spare_key !== undefined ? formData.has_spare_key : (formData.hasSpareKey || true),
      has_documents: formData.has_documents !== undefined ? formData.has_documents : (formData.hasDocuments || true),
      has_first_aid_kit: formData.has_first_aid_kit !== undefined ? formData.has_first_aid_kit : (formData.hasFirstAidKit || true),
      has_fire_extinguisher: formData.has_fire_extinguisher !== undefined ? formData.has_fire_extinguisher : (formData.hasFireExtinguisher || true),
      has_warning_triangle: formData.has_warning_triangle !== undefined ? formData.has_warning_triangle : (formData.hasWarningTriangle || true),
      has_photos: formData.has_photos !== undefined ? formData.has_photos : (formData.hasPhotos || false),
      photo_count: formData.photo_count || formData.photoCount || 0,
      photo_urls: formData.photo_urls || formData.photoUrls || null,
      receiving_notes: formData.receiving_notes || formData.receivingNotes || null,
      additional_notes: formData.additional_notes || formData.additionalNotes || null,
      driver_signature: formData.driver_signature || formData.driverSignature || null,
      secondary_driver_signature: formData.secondary_driver_signature || formData.secondaryDriverSignature || null,
      admin_signature: formData.admin_signature || formData.adminSignature || null,
      staff_name: formData.staff_name || formData.staffName || null,
      created_by: formData.created_by || formData.createdBy || null,
    };

    const { data, error } = await supabase
      .from('vehicle_receiving_forms')
      .insert([dbData])
      .select()
      .single();
    
    if (error) throw error;
    
    // Log receiving form creation
    await logActivity({
      entity_type: 'receiving_form',
      entity_id: data.id,
      action_type: 'create',
      changes: {
        after: {
          reference_number: data.reference_number,
          vehicle_id: data.vehicle_id,
          driver_id: data.driver_id,
          assignment_date: data.assignment_date,
          rent_amount: data.rent_amount,
        },
      },
      description: `Created receiving form ${data.reference_number}`,
    });
    
    return data;
  } catch (error) {
    console.error('Error creating vehicle receiving form:', error);
    throw error;
  }
};

// Vehicle Damages API
export const createVehicleDamage = async (damageData: any) => {
  const dbData = {
    receiving_form_id: damageData.receiving_form_id || damageData.receivingFormId || null,
    delivery_form_id: damageData.delivery_form_id || damageData.deliveryFormId || null,
    damage_type: damageData.damage_type || damageData.damageType || 'scratch',
    damage_severity: damageData.damage_severity || damageData.damageSeverity || 'minor',
    damage_location: damageData.damage_location || damageData.damageLocation || 'Unknown location',
    damage_description: damageData.damage_description || damageData.damageDescription || null,
    x_coordinate: damageData.x_coordinate || damageData.x || 0,
    y_coordinate: damageData.y_coordinate || damageData.y || 0,
    photo_url: damageData.photo_url || damageData.photoUrl || null,
  };

  const { data, error } = await supabase
    .from('vehicle_damages')
    .insert([dbData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Create multiple vehicle damages in batch (for forms with multiple damage markers)
export const createMultipleVehicleDamages = async (
  damageMarkers: any[], 
  formId: string, 
  formType: 'receiving' | 'delivery' = 'receiving'
) => {
  if (!damageMarkers || damageMarkers.length === 0) {
    return [];
  }

  // Convert damage markers to database format
  const dbDataArray = damageMarkers.map(marker => ({
    receiving_form_id: formType === 'receiving' ? formId : null,
    delivery_form_id: formType === 'delivery' ? formId : null,
    damage_type: marker.type,
    damage_severity: marker.severity,
    damage_location: `${marker.type} at (${marker.x}, ${marker.y})`, // Auto-generate location description
    damage_description: marker.description || null,
    x_coordinate: marker.x,
    y_coordinate: marker.y,
    photo_url: marker.photoUrl || null,
  }));

  const { data, error } = await supabase
    .from('vehicle_damages')
    .insert(dbDataArray)
    .select();
  
  if (error) throw error;
  return data;
};

// Vehicle Deliveries API
export const createVehicleDelivery = async (deliveryData: any) => {
  const dbData = {
    reference_number: deliveryData.reference_number || deliveryData.referenceNumber || `STSC-${Date.now()}-DEL`,
    assignment_id: deliveryData.assignment_id || deliveryData.assignmentId,
    driver_id: deliveryData.driver_id || deliveryData.driverId,
    vehicle_id: deliveryData.vehicle_id || deliveryData.vehicleId,
    delivery_date: deliveryData.delivery_date || deliveryData.deliveryDate,
    delivery_time: deliveryData.delivery_time || deliveryData.deliveryTime,
    final_mileage: deliveryData.final_mileage || deliveryData.finalMileage || parseInt(deliveryData.deliveryMileage) || 0,
    fuel_level: deliveryData.fuel_level || deliveryData.fuelLevel || parseInt(deliveryData.deliveryFuelLevel) || 50,
    exterior_condition: deliveryData.exterior_condition || deliveryData.exteriorCondition || null,
    interior_condition: deliveryData.interior_condition || deliveryData.interiorCondition || null,
    tire_condition: deliveryData.tire_condition || deliveryData.tireCondition || null,
    has_all_keys: deliveryData.has_all_keys !== undefined ? deliveryData.has_all_keys : (deliveryData.hasAllKeys || true),
    has_all_documents: deliveryData.has_all_documents !== undefined ? deliveryData.has_all_documents : (deliveryData.hasAllDocuments || true),
    has_spare_wheel: deliveryData.has_spare_wheel !== undefined ? deliveryData.has_spare_wheel : (deliveryData.hasSpareWheel || true),
    has_jack_and_tools: deliveryData.has_jack_and_tools !== undefined ? deliveryData.has_jack_and_tools : (deliveryData.hasJackAndTools || true),
    has_damages: deliveryData.has_damages !== undefined ? deliveryData.has_damages : false,
    driver_signature: deliveryData.driver_signature || deliveryData.driverSignature || deliveryData.deliveredBy || null,
    company_signature: deliveryData.company_signature || deliveryData.companySignature || deliveryData.receivedBy || null,
    additional_notes: deliveryData.additional_notes || deliveryData.additionalNotes || null,
    created_by: deliveryData.created_by || deliveryData.createdBy || null,
  };

  const { data, error } = await supabase
    .from('vehicle_delivery_forms')
    .insert([dbData])
    .select()
    .single();
  
  if (error) throw error;
  
  // Log delivery form creation
  try {
    const { logActivity } = require('./activityLog');
    await logActivity({
      entity_type: 'delivery_form',
      entity_id: data.id,
      action_type: 'create',
      changes: {
        after: {
          reference_number: data.reference_number,
          vehicle_id: data.vehicle_id,
          driver_id: data.driver_id,
          delivery_date: data.delivery_date,
          final_mileage: data.final_mileage,
        },
      },
      description: `Created delivery form ${data.reference_number}`,
    });
  } catch (logError) {
    console.warn('⚠️ Failed to log delivery form creation:', logError);
    // Don't throw - activity logging is optional
  }
  
  return data;
};

// Fetch functions for forms
export const getVehicleReceivingFormByAssignment = async (assignmentId: string, suffix?: 'A' | 'B') => {
  console.log('🔍 Looking for receiving form for assignment:', assignmentId, suffix ? `(Suffix: ${suffix})` : '');
  
  // First get the assignment to get vehicle_id and driver_id
  const { data: assignment, error: assignmentError } = await supabase
    .from('assignment_history')
    .select('vehicle_id, primary_driver_id, assignment_date')
    .eq('id', assignmentId)
    .single();
  
  if (assignmentError) {
    console.log('❌ Assignment not found:', assignmentError);
    throw assignmentError;
  }
  
  console.log('📋 Found assignment:', assignment);
  
  // Build query for receiving form by assignment_id
  let query = supabase
    .from('vehicle_receiving_forms')
    .select('*')
    .eq('assignment_id', assignmentId);
  
  // For partnership assignments, filter by reference_number suffix
  if (suffix) {
    query = query.like('reference_number', `%-${suffix}`);
  }
  
  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(1);
  
  if (error) {
    console.log('❌ Error fetching receiving form:', error);
    throw error;
  }
  
  console.log('📄 Found receiving form:', data?.[0] ? 'Yes' : 'No');
  
  // Return the first item or null if no receiving form exists
  return data && data.length > 0 ? data[0] : null;
};

export const getVehicleDeliveryFormByAssignment = async (assignmentId: string, suffix?: 'A' | 'B') => {
  // First get the assignment to get vehicle_id and driver_id
  const { data: assignment, error: assignmentError } = await supabase
    .from('assignment_history')
    .select('vehicle_id, primary_driver_id, assignment_date')
    .eq('id', assignmentId)
    .single();
  
  if (assignmentError) throw assignmentError;
  
  // Build query for delivery form by assignment_id
  let query = supabase
    .from('vehicle_delivery_forms')
    .select('*')
    .eq('assignment_id', assignmentId);
  
  // For partnership assignments, filter by reference_number suffix
  if (suffix) {
    query = query.like('reference_number', `%-${suffix}`);
  }
  
  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(1);
  
  if (error) throw error;
  
  // Return the first item or null if no delivery form exists
  return data && data.length > 0 ? data[0] : null;
};

// Hybrid version that checks both database and local storage
export const getVehicleDeliveryFormByAssignmentHybrid = async (assignmentId: string, suffix?: 'A' | 'B') => {
  try {
    // First try to get from database
    const dbResult = await getVehicleDeliveryFormByAssignment(assignmentId, suffix);
    if (dbResult) {
      console.log('🚚 Found delivery form in database:', dbResult.id);
      return dbResult;
    }
    
    // If not found in database, check local storage
    console.log('🚚 No delivery form found in database, checking local storage for assignment:', assignmentId);
    
    // Import HybridStorage to access localStorage
    const { hybridStorage } = await import('../services/offline/HybridStorage');
    
    // Get all delivery forms from local storage (no id = get all)
    const localForms = await hybridStorage.retrieve('deliveryForms');
    console.log('🚚 Local delivery forms found:', Array.isArray(localForms) ? localForms.length : 0);
    
    // If we get an array, find the matching form
    if (Array.isArray(localForms)) {
      const matchingForm = localForms.find((form: any) => {
        const assignmentMatches = form.assignment_id === assignmentId;
        // For partnerships, also check reference_number suffix
        if (suffix && assignmentMatches) {
          return form.reference_number?.endsWith(`-${suffix}`);
        }
        return assignmentMatches;
      });
      
      if (matchingForm) {
        console.log('🚚 Found delivery form in local storage:', matchingForm.id || 'local-id');
        return matchingForm;
      }
    }
    
    console.log('🚚 No delivery form found in database or local storage for assignment:', assignmentId);
    return null;
    
  } catch (error) {
    console.error('❌ Error in getVehicleDeliveryFormByAssignmentHybrid:', error);
    // Fallback to database-only version
    return await getVehicleDeliveryFormByAssignment(assignmentId, suffix);
  }
};

// Hybrid version for receiving forms that checks both database and local storage
export const getVehicleReceivingFormByAssignmentHybrid = async (assignmentId: string, suffix?: 'A' | 'B') => {
  try {
    // First try to get from database
    const dbResult = await getVehicleReceivingFormByAssignment(assignmentId, suffix);
    if (dbResult) {
      console.log('📋 Found receiving form in database:', dbResult.id);
      return dbResult;
    }
    
    // If not found in database, check local storage
    console.log('📋 No receiving form found in database, checking local storage for assignment:', assignmentId);
    
    // Import HybridStorage to access localStorage
    const { hybridStorage } = await import('../services/offline/HybridStorage');
    
    // Get all receiving forms from local storage (no id = get all)
    const localForms = await hybridStorage.retrieve('receivingForms');
    console.log('📋 Local receiving forms found:', Array.isArray(localForms) ? localForms.length : 0);
    
    // If we get an array, find the matching form
    if (Array.isArray(localForms)) {
      const matchingForm = localForms.find((form: any) => {
        const assignmentMatches = form.assignment_id === assignmentId;
        // For partnerships, also check reference_number suffix
        if (suffix && assignmentMatches) {
          return form.reference_number?.endsWith(`-${suffix}`);
        }
        return assignmentMatches;
      });
      
      if (matchingForm) {
        console.log('📋 Found receiving form in local storage:', matchingForm.id || 'local-id');
        return matchingForm;
      }
    }
    
    console.log('📋 No receiving form found in database or local storage for assignment:', assignmentId);
    return null;
    
  } catch (error) {
    console.error('❌ Error in getVehicleReceivingFormByAssignmentHybrid:', error);
    // Fallback to database-only version
    return await getVehicleReceivingFormByAssignment(assignmentId, suffix);
  }
};

export const getVehicleDamagesByReceivingForm = async (receivingFormId: string) => {
  const { data, error } = await supabase
    .from('vehicle_damages')
    .select('*')
    .eq('receiving_form_id', receivingFormId);
  
  if (error) throw error;
  
  // Convert database records back to damage marker format
  return data?.map(damage => ({
    id: damage.id,
    x: damage.x_coordinate || 0,
    y: damage.y_coordinate || 0,
    type: damage.damage_type,
    severity: damage.damage_severity || 'minor',
    description: damage.damage_description,
    photoUrl: damage.photo_url, // Include photo URL for display
    timestamp: damage.created_at,
  })) || [];
};

export const getVehicleDamagesByDeliveryForm = async (deliveryFormId: string) => {
  const { data, error } = await supabase
    .from('vehicle_damages')
    .select('*')
    .eq('delivery_form_id', deliveryFormId);
  
  if (error) throw error;
  
  // Convert database records back to damage marker format
  return data?.map(damage => ({
    id: damage.id,
    x: damage.x_coordinate || 0,
    y: damage.y_coordinate || 0,
    type: damage.damage_type,
    severity: damage.damage_severity || 'minor',
    description: damage.damage_description,
    photoUrl: damage.photo_url, // Include photo URL for display
    timestamp: damage.created_at,
  })) || [];
};

// Hybrid version that checks both database and local storage for delivery form damages
export const getVehicleDamagesByDeliveryFormHybrid = async (deliveryFormId: string) => {
  try {
    // Check if this is a local storage ID (contains underscores and starts with known prefixes)
    const isLocalId = deliveryFormId.includes('_') && (
      deliveryFormId.startsWith('deliveryForms_') || 
      deliveryFormId.startsWith('receivingForms_')
    );
    
    if (!isLocalId) {
      // Only try database if this looks like a UUID
      const dbResult = await getVehicleDamagesByDeliveryForm(deliveryFormId);
      if (dbResult && dbResult.length > 0) {
        console.log('🚚 Found delivery form damages in database:', dbResult.length);
        return dbResult;
      }
    }
    
    // Check local storage (either because database failed or this is a local ID)
    console.log('🚚 Checking local storage for delivery form damages:', deliveryFormId);
    
    // Import HybridStorage to access localStorage
    const { hybridStorage } = await import('../services/offline/HybridStorage');
    
    // Get all damages from local storage (no id = get all)
    const localDamages = await hybridStorage.retrieve('damages');
    console.log('🚚 Local damages found:', Array.isArray(localDamages) ? localDamages.length : 0);
    
    // If we get an array, find damages that match this delivery form ID
    if (Array.isArray(localDamages)) {
      const matchingDamages = localDamages.filter((damage: any) => 
        damage.delivery_form_id === deliveryFormId
      );
      
      if (matchingDamages.length > 0) {
        console.log('🚚 Found delivery form damages in local storage:', matchingDamages.length);
        
        // Convert to the expected format
        return matchingDamages.map((damage: any) => ({
          id: damage.id,
          x: damage.x_coordinate || damage.x || 0,
          y: damage.y_coordinate || damage.y || 0,
          type: damage.damage_type || damage.type,
          severity: damage.damage_severity || damage.severity || 'minor',
          description: damage.damage_description || damage.description,
          timestamp: damage.created_at || damage.timestamp,
        }));
      }
    }
    
    console.log('🚚 No damages found in database or local storage for delivery form:', deliveryFormId);
    return [];
    
  } catch (error) {
    console.error('❌ Error in getVehicleDamagesByDeliveryFormHybrid:', error);
    // Fallback to database-only version
    return await getVehicleDamagesByDeliveryForm(deliveryFormId);
  }
};

// Debug function to check all damages in the database
export const getAllVehicleDamages = async () => {
  const { data, error } = await supabase
    .from('vehicle_damages')
    .select('*')
    .limit(10); // Limit to avoid large datasets
  
  if (error) throw error;
  return data || [];
};

// Alternative function to get damages by assignment ID
export const getVehicleDamagesByAssignment = async (assignmentId: string) => {
  // First get the receiving form for this assignment
  try {
    const receivingFormData = await getVehicleReceivingFormByAssignment(assignmentId);
    if (receivingFormData?.id) {
      return await getVehicleDamagesByReceivingForm(receivingFormData.id);
    }
  } catch (error) {
    console.warn('Could not get receiving form for assignment:', assignmentId);
  }
  
  // If no receiving form found, try to find damages by other means
  // This would require additional relationships or logic
  return [];
};

// Test function to create sample damage markers for demonstration
export const createSampleDamageMarkers = async (receivingFormId: string) => {
  const sampleDamages = [
    {
      receivingFormId: receivingFormId,
      damageType: 'scratch',
      damageSeverity: 'minor',
      damageLocation: 'Front bumper',
      damageDescription: 'Small scratch on left side of front bumper',
      x: 175, // Center-left of vehicle image
      y: 50,  // Front area
    },
    {
      receivingFormId: receivingFormId,
      damageType: 'dent',
      damageSeverity: 'moderate', 
      damageLocation: 'Rear door',
      damageDescription: 'Small dent on passenger side rear door',
      x: 280, // Right side of vehicle
      y: 150, // Middle area
    },
    {
      receivingFormId: receivingFormId,
      damageType: 'missing',
      damageSeverity: 'major',
      damageLocation: 'Side mirror',
      damageDescription: 'Driver side mirror is missing',
      x: 80,  // Left side
      y: 100, // Mirror area
    }
  ];
  
  console.log('🔧 Creating sample damage markers for receiving form:', receivingFormId);
  const results = [];
  for (const damage of sampleDamages) {
    try {
      const result = await createVehicleDamage(damage);
      results.push(result);
      console.log('✅ Created sample damage:', damage.damageLocation);
    } catch (error) {
      console.error('❌ Error creating sample damage:', error);
    }
  }
  
  return results;
};

// ===== ACCOUNTING FUNCTIONS =====

/**
 * Get accounting transactions with optional filters
 */
export async function getAccountingTransactions(filters?: {
  startDate?: string;
  endDate?: string;
  driverId?: string;
  vehicleId?: string;
  type?: string;
  status?: string;
  limit?: number;
}) {
  let query = supabase
    .from('accounting_transactions')
    .select(`
      id,
      driver_id,
      vehicle_id,
      type,
      category,
      amount,
      description,
      transaction_date,
      status,
      reference_number,
      payment_method,
      notes,
      created_at,
      updated_at,
      drivers(id, name, phone),
      vehicles(id, license_plate, make_model)
    `)
    .order('transaction_date', { ascending: false });

  if (filters?.startDate) {
    query = query.gte('transaction_date', filters.startDate);
  }
  if (filters?.endDate) {
    query = query.lte('transaction_date', filters.endDate);
  }
  if (filters?.driverId) {
    query = query.eq('driver_id', filters.driverId);
  }
  if (filters?.vehicleId) {
    query = query.eq('vehicle_id', filters.vehicleId);
  }
  if (filters?.type) {
    query = query.eq('type', filters.type);
  }
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }
  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  const { data, error } = await query;
  if (error) throw error;
  
  // Transform data to match frontend interface
  return data?.map(transaction => ({
    id: transaction.id,
    transactionDate: transaction.transaction_date,
    type: transaction.type,
    category: transaction.category,
    amount: transaction.amount,
    description: transaction.description,
    referenceNumber: transaction.reference_number,
    paymentMethod: transaction.payment_method,
    status: transaction.status,
    driverId: transaction.driver_id,
    vehicleId: transaction.vehicle_id,
    notes: transaction.notes,
    createdAt: transaction.created_at,
    updatedAt: transaction.updated_at,
    driver: transaction.drivers?.[0],
    vehicle: transaction.vehicles?.[0]
  })) || [];
}

/**
 * Get driver balances summary with correct column names
 */
export async function getDriverBalances() {
  const { data, error } = await supabase
    .from('driver_balances')
    .select(`
      id,
      driver_id,
      current_balance,
      total_careem_income,
      total_uber_income,
      total_rent_charged,
      total_fines,
      total_salik,
      total_payments_received,
      total_other_expenses,
      last_calculated_at,
      calculation_method,
      created_at,
      updated_at,
      drivers(id, name, phone)
    `)
    .order('current_balance', { ascending: false });

  if (error) throw error;
  
  // Transform data to match frontend interface
  return data?.map(balance => ({
    driverId: balance.driver_id,
    currentBalance: balance.current_balance || 0,
    totalEarned: (balance.total_careem_income || 0) + (balance.total_uber_income || 0),
    totalOwed: (balance.total_fines || 0) + (balance.total_salik || 0) + (balance.total_other_expenses || 0) + (balance.total_rent_charged || 0),
    pendingDeductions: 0, // Calculate if needed
    totalCareemIncome: balance.total_careem_income || 0,
    totalUberIncome: balance.total_uber_income || 0,
    totalFines: balance.total_fines || 0,
    totalRentCharged: balance.total_rent_charged || 0,
    totalSalik: balance.total_salik || 0,
    totalPaymentsReceived: balance.total_payments_received || 0,
    totalOtherExpenses: balance.total_other_expenses || 0,
    lastPaymentDate: null, // Not available in current schema
    lastCalculationDate: balance.last_calculated_at,
    updatedAt: balance.updated_at,
    driver: balance.drivers
  })) || [];
}

/**
 * Get accounting summary for dashboard with correct column names
 */
export async function getAccountingSummary(period?: { startDate?: string; endDate?: string }) {
  try {
    // Get current month if no period specified
    const now = new Date();
    const startDate = period?.startDate || new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const endDate = period?.endDate || now.toISOString().split('T')[0];

    // Get transactions for the period
    const { data: transactions, error: transError } = await supabase
      .from('accounting_transactions')
      .select('type, amount, status')
      .gte('transaction_date', startDate)
      .lte('transaction_date', endDate);

    if (transError) throw transError;

    // Get driver balances
    const { data: balances, error: balanceError } = await supabase
      .from('driver_balances')
      .select('current_balance, total_careem_income, total_uber_income, total_fines, total_salik, total_other_expenses, total_rent_charged');

    if (balanceError) throw balanceError;

    // Calculate summary with safe null handling - Fix: Use 'earning' instead of 'income'
    const totalIncome = transactions
      ?.filter(t => t.type === 'earning' && t.status === 'completed')
      .reduce((sum, t) => sum + (t.amount || 0), 0) || 0;

    const totalExpenses = transactions
      ?.filter(t => t.type === 'expense' && t.status === 'completed')
      .reduce((sum, t) => sum + (t.amount || 0), 0) || 0;

    const pendingTransactions = transactions
      ?.filter(t => t.status === 'pending').length || 0;

    const driversWithNegativeBalance = balances
      ?.filter(b => (b.current_balance || 0) < 0).length || 0;

    // Sum up total system balances
    const totalSystemBalance = balances
      ?.reduce((sum, b) => sum + (b.current_balance || 0), 0) || 0;

    const totalCareemIncome = balances
      ?.reduce((sum, b) => sum + (b.total_careem_income || 0), 0) || 0;

    const totalUberIncome = balances
      ?.reduce((sum, b) => sum + (b.total_uber_income || 0), 0) || 0;

    const totalFines = balances
      ?.reduce((sum, b) => sum + (b.total_fines || 0), 0) || 0;

    const totalSalik = balances
      ?.reduce((sum, b) => sum + (b.total_salik || 0), 0) || 0;

    const totalOtherExpenses = balances
      ?.reduce((sum, b) => sum + (b.total_other_expenses || 0), 0) || 0;

    const totalRentCharged = balances
      ?.reduce((sum, b) => sum + (b.total_rent_charged || 0), 0) || 0;

    // Calculate totals from balances (more accurate than transactions)
    const totalEarned = totalCareemIncome + totalUberIncome;
    const totalOwed = totalFines + totalSalik + totalOtherExpenses + totalRentCharged;

    return {
      totalIncome: totalEarned, // Use calculated earned from balances
      totalExpenses: totalOwed, // Use calculated owed from balances
      netProfit: totalEarned - totalOwed,
      pendingTransactions,
      driversWithNegativeBalance,
      totalSystemBalance,
      totalCareemIncome,
      totalUberIncome,
      totalFines,
      totalEarned,
      totalOwed,
      monthlyRevenue: totalEarned,
      monthlyExpenses: totalOwed,
      periodStart: startDate,
      periodEnd: endDate,
      recordCount: transactions?.length || 0,
      driverCount: balances?.length || 0
    };
  } catch (error) {
    console.error('Error getting accounting summary:', error);
    // Return safe defaults if query fails
    return {
      totalIncome: 0,
      totalExpenses: 0,
      netProfit: 0,
      pendingTransactions: 0,
      driversWithNegativeBalance: 0,
      totalSystemBalance: 0,
      totalCareemIncome: 0,
      totalUberIncome: 0,
      totalFines: 0,
      totalEarned: 0,
      totalOwed: 0,
      monthlyRevenue: 0,
      monthlyExpenses: 0,
      periodStart: period?.startDate || new Date().toISOString().split('T')[0],
      periodEnd: period?.endDate || new Date().toISOString().split('T')[0],
      recordCount: 0,
      driverCount: 0
    };
  }
}

/**
 * Create a new accounting transaction
 */
export async function createAccountingTransaction(transaction: {
  transactionDate: string;
  vehicleId?: string;
  driverId?: string;
  type: 'income' | 'expense' | 'fine' | 'salik';
  category: string;
  amount: number;
  description?: string;
  referenceNumber?: string;
  paymentMethod?: string;
  status?: string;
  importBatchId?: string;
  notes?: string;
  createdBy?: string;
}) {
  const { data, error } = await supabase
    .from('accounting_transactions')
    .insert({
      transaction_date: transaction.transactionDate,
      vehicle_id: transaction.vehicleId,
      driver_id: transaction.driverId,
      type: transaction.type,
      category: transaction.category,
      amount: transaction.amount,
      description: transaction.description,
      reference_number: transaction.referenceNumber,
      payment_method: transaction.paymentMethod,
      status: transaction.status || 'pending',
      import_batch_id: transaction.importBatchId,
      notes: transaction.notes,
      created_by: transaction.createdBy
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update an existing accounting transaction
 */
export async function updateAccountingTransaction(id: string, updates: Partial<{
  transactionDate: string;
  vehicleId: string;
  driverId: string;
  type: 'income' | 'expense' | 'fine' | 'salik';
  category: string;
  amount: number;
  description: string;
  referenceNumber: string;
  paymentMethod: string;
  status: string;
  notes: string;
}>) {
  const dbUpdates: any = {};
  if (updates.transactionDate) dbUpdates.transaction_date = updates.transactionDate;
  if (updates.vehicleId) dbUpdates.vehicle_id = updates.vehicleId;
  if (updates.driverId) dbUpdates.driver_id = updates.driverId;
  if (updates.type) dbUpdates.type = updates.type;
  if (updates.category) dbUpdates.category = updates.category;
  if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
  if (updates.description) dbUpdates.description = updates.description;
  if (updates.referenceNumber) dbUpdates.reference_number = updates.referenceNumber;
  if (updates.paymentMethod) dbUpdates.payment_method = updates.paymentMethod;
  if (updates.status) dbUpdates.status = updates.status;
  if (updates.notes) dbUpdates.notes = updates.notes;
  dbUpdates.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from('accounting_transactions')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete an accounting transaction
 */
export async function deleteAccountingTransaction(id: string) {
  const { error } = await supabase
    .from('accounting_transactions')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
}

/**
 * Record a payment from a driver (to reduce their debt)
 */
export async function recordDriverPayment(
  driverId: string,
  paymentAmount: number,
  paymentMethod: 'cash' | 'bank_transfer' | 'deposit',
  referenceNumber?: string
) {
  try {
    // Get current driver balance
    const { data: currentBalance, error: balanceError } = await supabase
      .from('driver_balances')
      .select('current_balance, driver_id, total_payments_received, drivers(name)')
      .eq('driver_id', driverId)
      .single();

    if (balanceError) throw balanceError;

    // Validate payment
    if (paymentAmount <= 0) {
      throw new Error('Payment amount must be greater than zero');
    }

    if (currentBalance.current_balance >= 0) {
      throw new Error('Driver has no outstanding debt to pay');
    }

    const debt = Math.abs(currentBalance.current_balance);
    if (paymentAmount > debt) {
      throw new Error(`Payment amount (${paymentAmount}) cannot exceed debt (${debt})`);
    }

    // Create payment transaction record
    const { data: transaction, error: transactionError } = await supabase
      .from('accounting_transactions')
      .insert({
        transaction_date: new Date().toISOString().split('T')[0],
        driver_id: driverId,
        type: 'income',
        category: 'driver_payment',
        amount: paymentAmount,
        description: `Payment received from ${(currentBalance as any).drivers?.name || 'driver'}`,
        reference_number: referenceNumber,
        payment_method: paymentMethod,
        status: 'completed',
        notes: `Driver debt payment - ${paymentMethod.replace('_', ' ')}`
      })
      .select()
      .single();

    if (transactionError) throw transactionError;

    // Update driver balance
    const newBalance = currentBalance.current_balance + paymentAmount;
    const { error: updateError } = await supabase
      .from('driver_balances')
      .update({
        current_balance: newBalance,
        total_payments_received: (currentBalance.total_payments_received || 0) + paymentAmount,
        last_calculated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('driver_id', driverId);

    if (updateError) throw updateError;

    return {
      success: true,
      transaction,
      previousBalance: currentBalance.current_balance,
      newBalance,
      paymentAmount,
      remainingDebt: newBalance < 0 ? Math.abs(newBalance) : 0
    };

  } catch (error) {
    console.error('Error recording driver payment:', error);
    throw error;
  }
}

/**
 * Record a payout to a driver (when company owes money to driver)
 */
export async function recordDriverPayout(
  driverId: string,
  payoutAmount: number,
  paymentMethod: 'bank_transfer' | 'cash' | 'check',
  referenceNumber?: string,
  bankDetails?: {
    accountNumber?: string;
    bankName?: string;
    iban?: string;
  }
) {
  try {
    // Get current driver balance
    const { data: currentBalance, error: balanceError } = await supabase
      .from('driver_balances')
      .select('current_balance, driver_id, total_payments_received, drivers(name)')
      .eq('driver_id', driverId)
      .single();

    if (balanceError) throw balanceError;

    // Validate payout
    if (payoutAmount <= 0) {
      throw new Error('Payout amount must be greater than zero');
    }

    if (currentBalance.current_balance <= 0) {
      throw new Error('Driver has no credit balance to receive payout');
    }

    const creditBalance = currentBalance.current_balance;
    if (payoutAmount > creditBalance) {
      throw new Error(`Payout amount (${payoutAmount}) cannot exceed credit balance (${creditBalance})`);
    }

    // Create payout transaction record
    const { data: transaction, error: transactionError } = await supabase
      .from('accounting_transactions')
      .insert({
        transaction_date: new Date().toISOString().split('T')[0],
        driver_id: driverId,
        type: 'expense',
        category: 'driver_payout',
        amount: payoutAmount,
        description: `Payout to ${(currentBalance as any).drivers?.name || 'driver'}`,
        reference_number: referenceNumber,
        payment_method: paymentMethod,
        status: 'completed',
        notes: `Driver credit payout - ${paymentMethod.replace('_', ' ')}${bankDetails ? ` to ${bankDetails.bankName || 'bank'}` : ''}`
      })
      .select()
      .single();

    if (transactionError) throw transactionError;

    // Update driver balance
    const newBalance = currentBalance.current_balance - payoutAmount;
    const { error: updateError } = await supabase
      .from('driver_balances')
      .update({
        current_balance: newBalance,
        last_calculated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('driver_id', driverId);

    if (updateError) throw updateError;

    return {
      success: true,
      transaction,
      previousBalance: currentBalance.current_balance,
      newBalance,
      payoutAmount,
      remainingCredit: newBalance > 0 ? newBalance : 0
    };

  } catch (error) {
    console.error('Error recording driver payout:', error);
    throw error;
  }
}

/**
 * Get a single driver's balance and transaction summary
 */
export async function getDriverStatement(driverId: string, period?: { startDate?: string; endDate?: string }) {
  try {
    // Get driver details
    const { data: driver, error: driverError } = await supabase
      .from('drivers')
      .select('id, name, phone')
      .eq('id', driverId)
      .single();

    if (driverError) throw driverError;

    // Get driver balance
    const { data: balance, error: balanceError } = await supabase
      .from('driver_balances')
      .select('*')
      .eq('driver_id', driverId)
      .single();

    if (balanceError && balanceError.code !== 'PGRST116') throw balanceError;

    // Get driver transactions for the period
    let transactionQuery = supabase
      .from('accounting_transactions')
      .select(`
        id,
        transaction_date,
        type,
        category,
        amount,
        description,
        reference_number,
        status,
        notes,
        vehicles(license_plate, make_model)
      `)
      .eq('driver_id', driverId)
      .order('transaction_date', { ascending: false });

    if (period?.startDate) {
      transactionQuery = transactionQuery.gte('transaction_date', period.startDate);
    }
    if (period?.endDate) {
      transactionQuery = transactionQuery.lte('transaction_date', period.endDate);
    }

    const { data: transactions, error: transError } = await transactionQuery;
    if (transError) throw transError;

    // Calculate summary for the period
    const totalIncome = transactions?.filter(t => t.type === 'income').reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
    const totalExpenses = transactions?.filter(t => ['expense', 'fine', 'salik'].includes(t.type)).reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
    const pendingAmount = transactions?.filter(t => t.status === 'pending').reduce((sum, t) => sum + (t.amount || 0), 0) || 0;

    return {
      driver,
      balance: balance || {
        current_balance: 0,
        total_earned: 0,
        total_owed: 0,
        pending_deductions: 0
      },
      transactions: transactions || [],
      summary: {
        totalIncome,
        totalExpenses,
        netBalance: totalIncome - totalExpenses,
        pendingAmount
      },
      period: {
        startDate: period?.startDate || 'All time',
        endDate: period?.endDate || 'Now'
      }
    };
  } catch (error) {
    console.error('Error getting driver statement:', error);
    throw error;
  }
}

/**
 * Generate daily vehicle rent entries for all active assignments
 */
export async function generateDailyVehicleRentEntries(date: string) {
  try {
    // Call the stored procedure that was created in GENERATE_DAILY_RENT.sql
    const { data, error } = await supabase.rpc('generate_daily_rent_entries', {
      p_date: date
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error generating daily rent entries:', error);
    throw error;
  }
}

/**
 * Process driver payment (cash collection, bank transfer, etc.)
 */
export async function processDriverPayment(driverId: string, amount: number, paymentMethod: string, notes?: string) {
  try {
    const { data, error } = await supabase
      .from('accounting_transactions')
      .insert({
        transaction_date: new Date().toISOString().split('T')[0],
        driver_id: driverId,
        type: 'income',
        category: 'driver_payment',
        amount: amount,
        description: `Driver payment via ${paymentMethod}`,
        payment_method: paymentMethod,
        status: 'completed',
        notes: notes
      })
      .select()
      .single();

    if (error) throw error;

    // Recalculate driver balance
    await recalculateDriverBalance(driverId);

    return data;
  } catch (error) {
    console.error('Error processing driver payment:', error);
    throw error;
  }
}

/**
 * Get recent activity for dashboard
 */
export async function getRecentAccountingActivity(limit: number = 10) {
  try {
    const { data, error } = await supabase
      .from('accounting_transactions')
      .select(`
        id,
        transaction_date,
        type,
        category,
        amount,
        description,
        status,
        drivers(name),
        vehicles(license_plate)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return data?.map(transaction => ({
      id: transaction.id,
      date: transaction.transaction_date,
      type: transaction.type,
      category: transaction.category,
      amount: transaction.amount,
      description: transaction.description,
      status: transaction.status,
      driverName: transaction.drivers?.[0]?.name,
      vehiclePlate: transaction.vehicles?.[0]?.license_plate
    })) || [];
  } catch (error) {
    console.error('Error getting recent activity:', error);
    return [];
  }
}

/**
 * Search transactions with text query
 */
export async function searchAccountingTransactions(query: string, filters?: {
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}) {
  try {
    let searchQuery = supabase
      .from('accounting_transactions')
      .select(`
        id,
        transaction_date,
        type,
        category,
        amount,
        description,
        reference_number,
        status,
        drivers(name, phone),
        vehicles(license_plate, make_model)
      `);

    // Text search in description, reference_number, or category
    if (query.trim()) {
      searchQuery = searchQuery.or(`description.ilike.%${query}%,reference_number.ilike.%${query}%,category.ilike.%${query}%`);
    }

    // Apply filters
    if (filters?.type) {
      searchQuery = searchQuery.eq('type', filters.type);
    }
    if (filters?.status) {
      searchQuery = searchQuery.eq('status', filters.status);
    }
    if (filters?.startDate) {
      searchQuery = searchQuery.gte('transaction_date', filters.startDate);
    }
    if (filters?.endDate) {
      searchQuery = searchQuery.lte('transaction_date', filters.endDate);
    }

    searchQuery = searchQuery.order('transaction_date', { ascending: false }).limit(100);

    const { data, error } = await searchQuery;
    if (error) throw error;

    return data?.map(transaction => ({
      id: transaction.id,
      transactionDate: transaction.transaction_date,
      type: transaction.type,
      category: transaction.category,
      amount: transaction.amount,
      description: transaction.description,
      referenceNumber: transaction.reference_number,
      status: transaction.status,
      driver: transaction.drivers,
      vehicle: transaction.vehicles
    })) || [];
  } catch (error) {
    console.error('Error searching transactions:', error);
    return [];
  }
}

// ===== BALANCE MANAGEMENT FUNCTIONS =====

/**
 * Initialize driver balances for all drivers
 */
export async function initializeDriverBalances() {
  try {
    // Get all drivers
    const { data: drivers, error: driversError } = await supabase
      .from('drivers')
      .select('id, name');

    if (driversError) throw driversError;

    const results = {
      processed: 0,
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const driver of drivers) {
      try {
        results.processed++;

        // Check if balance already exists
        const { data: existingBalance } = await supabase
          .from('driver_balances')
          .select('driver_id')
          .eq('driver_id', driver.id)
          .single();

        if (!existingBalance) {
          // Create initial balance record
          const { error: insertError } = await supabase
            .from('driver_balances')
            .insert({
              driver_id: driver.id,
              current_balance: 0,
              total_earned: 0,
              total_owed: 0,
              pending_deductions: 0,
              total_careem_income: 0,
              total_uber_income: 0,
              total_fines: 0,
              last_calculation_date: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (insertError) throw insertError;
        }

        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`Failed to initialize balance for ${driver.name}: ${error}`);
      }
    }

    return results;
  } catch (error) {
    console.error('Error initializing driver balances:', error);
    throw error;
  }
}

/**
 * Recalculate driver balance based on transactions
 */
export async function recalculateDriverBalance(driverId: string) {
  try {
    // Get all transactions for this driver
    const { data: transactions, error: transError } = await supabase
      .from('accounting_transactions')
      .select('type, amount, status, category')
      .eq('driver_id', driverId);

    if (transError) throw transError;

    // Calculate totals
    const completedTransactions = transactions?.filter(t => t.status === 'completed') || [];
    const pendingTransactions = transactions?.filter(t => t.status === 'pending') || [];

    // Fix: Use 'earning' type instead of 'income' as per database constraint
    const totalEarned = completedTransactions
      .filter(t => t.type === 'earning')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalOwed = completedTransactions
      .filter(t => ['expense', 'fine', 'salik'].includes(t.type))
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const pendingDeductions = pendingTransactions
      .filter(t => ['expense', 'fine', 'salik'].includes(t.type))
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // Calculate specific totals - Fix: Use 'earning' type and correct categories
    const totalCareemIncome = completedTransactions
      .filter(t => t.type === 'earning' && (t.category === 'careem_payment' || t.category === 'careem_income'))
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalUberIncome = completedTransactions
      .filter(t => t.type === 'earning' && (t.category === 'uber_payment' || t.category === 'uber_income'))
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalFines = completedTransactions
      .filter(t => t.type === 'expense' && t.category === 'traffic_fine')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalSalik = completedTransactions
      .filter(t => t.type === 'expense' && t.category === 'salik_toll')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalRentCharged = completedTransactions
      .filter(t => t.type === 'expense' && t.category === 'daily_rent')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const totalOtherExpenses = completedTransactions
      .filter(t => t.type === 'expense' && !['traffic_fine', 'salik_toll', 'daily_rent'].includes(t.category))
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const currentBalance = totalEarned - totalOwed;

    // Update or insert balance record with correct column mappings
    const { error: upsertError } = await supabase
      .from('driver_balances')
      .upsert({
        driver_id: driverId,
        current_balance: currentBalance,
        total_careem_income: totalCareemIncome,
        total_uber_income: totalUberIncome,
        total_rent_charged: totalRentCharged,
        total_fines: totalFines,
        total_salik: totalSalik,
        total_other_expenses: totalOtherExpenses,
        last_calculated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

    if (upsertError) throw upsertError;

    return {
      driverId,
      currentBalance,
      totalEarned,
      totalOwed,
      pendingDeductions,
      totalCareemIncome,
      totalUberIncome,
      totalFines,
      totalSalik,
      totalRentCharged,
      totalOtherExpenses
    };
  } catch (error) {
    console.error('Error recalculating driver balance:', error);
    throw error;
  }
}

/**
 * Recalculate all driver balances
 */
export async function recalculateAllDriverBalances() {
  try {
    const { data: drivers, error: driversError } = await supabase
      .from('drivers')
      .select('id, name');

    if (driversError) throw driversError;

    const results = {
      processed: 0,
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const driver of drivers) {
      try {
        results.processed++;
        await recalculateDriverBalance(driver.id);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`Failed to recalculate balance for ${driver.name}: ${error}`);
      }
    }

    return results;
  } catch (error) {
    console.error('Error recalculating all driver balances:', error);
    throw error;
  }
}

// ============================================
// RAW DATA RETRIEVAL FUNCTIONS
// ============================================

/**
 * Get Careem income data with driver details
 */
export async function getCareemData(filters?: {
  startDate?: string;
  endDate?: string;
  driverId?: string;
  limit?: number;
}) {
  try {
    let query = supabase
      .from('careem_income_raw')
      .select(`
        *,
        drivers!driver_id (
          id,
          name,
          license_number,
          phone,
          vehicle_assignments!vehicle_assignments_driver_id_fkey (
            vehicle:vehicles (
              id,
              make,
              model,
              plate_number
            )
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('report_week_start', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('report_week_end', filters.endDate);
    }

    if (filters?.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching Careem data:', error);
    throw error;
  }
}

/**
 * Get Uber income data with driver details
 */
export async function getUberData(filters?: {
  startDate?: string;
  endDate?: string;
  driverId?: string;
  limit?: number;
}) {
  try {
    let query = supabase
      .from('uber_income_raw')
      .select(`
        *,
        drivers!driver_id (
          id,
          name,
          license_number,
          phone,
          vehicle_assignments!vehicle_assignments_driver_id_fkey (
            vehicle:vehicles (
              id,
              make,
              model,
              plate_number
            )
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('period_start', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('period_end', filters.endDate);
    }

    if (filters?.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching Uber data:', error);
    throw error;
  }
}

/**
 * Get Salik toll data with driver details
 */
export async function getSalikData(filters?: {
  startDate?: string;
  endDate?: string;
  driverId?: string;
  plateNumber?: string;
  limit?: number;
}) {
  try {
    let query = supabase
      .from('salik_tolls')
      .select(`
        *,
        drivers!driver_id (
          id,
          name,
          license_number,
          phone,
          vehicle_assignments!vehicle_assignments_driver_id_fkey (
            vehicle:vehicles (
              id,
              make,
              model,
              plate_number
            )
          )
        )
      `)
      .order('transaction_date', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('transaction_date', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('transaction_date', filters.endDate);
    }

    if (filters?.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    if (filters?.plateNumber) {
      query = query.ilike('license_plate', `%${filters.plateNumber}%`);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching Salik data:', error);
    throw error;
  }
}

/**
 * Get traffic fines data with driver details
 */
export async function getTrafficFinesData(filters?: {
  startDate?: string;
  endDate?: string;
  driverId?: string;
  plateNumber?: string;
  fineType?: string;
  limit?: number;
}) {
  try {
    let query = supabase
      .from('traffic_fines')
      .select(`
        *,
        drivers!driver_id (
          id,
          name,
          license_number,
          phone,
          vehicle_assignments!vehicle_assignments_driver_id_fkey (
            vehicle:vehicles (
              id,
              make,
              model,
              plate_number
            )
          )
        )
      `)
      .order('issue_date', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('issue_date', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('issue_date', filters.endDate);
    }

    if (filters?.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    if (filters?.plateNumber) {
      query = query.ilike('license_plate', `%${filters.plateNumber}%`);
    }

    if (filters?.fineType) {
      query = query.ilike('fine_place', `%${filters.fineType}%`);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching traffic fines data:', error);
    throw error;
  }
}

/**
 * Get summary statistics for all data tables
 */
export async function getDataTablesSummary() {
  try {
    const [careemStats, uberStats, salikStats, finesStats] = await Promise.all([
      // Careem statistics
      supabase
        .from('careem_income_raw')
        .select('total_earnings, created_at')
        .then(({ data, error }) => {
          if (error) throw error;
          const total = data?.reduce((sum, record) => sum + (parseFloat(record.total_earnings?.toString() || '0')), 0) || 0;
          return {
            table: 'Careem',
            totalRecords: data?.length || 0,
            totalAmount: total,
            latestDate: data?.length ? Math.max(...data.map(r => new Date(r.created_at).getTime())) : null,
            icon: 'car' as const,
            color: '#00C853' as const
          };
        }),

      // Uber statistics  
      supabase
        .from('uber_income_raw')
        .select('total_income, created_at')
        .then(({ data, error }) => {
          if (error) throw error;
          const total = data?.reduce((sum, record) => sum + (parseFloat(record.total_income?.toString() || '0')), 0) || 0;
          return {
            table: 'Uber',
            totalRecords: data?.length || 0,
            totalAmount: total,
            latestDate: data?.length ? Math.max(...data.map(r => new Date(r.created_at).getTime())) : null,
            icon: 'car-sport' as const,
            color: '#000000' as const
          };
        }),

      // Salik statistics
      supabase
        .from('salik_tolls')
        .select('amount, transaction_date')
        .then(({ data, error }) => {
          if (error) throw error;
          const total = data?.reduce((sum, record) => sum + (parseFloat(record.amount?.toString() || '0')), 0) || 0;
          return {
            table: 'Salik',
            totalRecords: data?.length || 0,
            totalAmount: total,
            latestDate: data?.length ? Math.max(...data.map(r => new Date(r.transaction_date).getTime())) : null,
            icon: 'radio' as const,
            color: '#2196F3' as const
          };
        }),

      // Traffic fines statistics
      supabase
        .from('traffic_fines')
        .select('amount_after_discount, issue_date')
        .then(({ data, error }) => {
          if (error) throw error;
          const total = data?.reduce((sum, record) => sum + (parseFloat(record.amount_after_discount?.toString() || '0')), 0) || 0;
          return {
            table: 'Fines',
            totalRecords: data?.length || 0,
            totalAmount: total,
            latestDate: data?.length ? Math.max(...data.map(r => new Date(r.issue_date).getTime())) : null,
            icon: 'warning' as const,
            color: '#F44336' as const
          };
        })
    ]);

    return [careemStats, uberStats, salikStats, finesStats];
  } catch (error) {
    console.error('Error fetching data tables summary:', error);
    throw error;
  }
}

// Raw Data Retrieval Functions for Accounting View
export const getCaremRawData = async (filters: {
  dateFrom?: string;
  dateTo?: string;
  processed?: boolean;
  driverId?: string;
} = {}) => {
  try {
    let query = supabase
      .from('careem_income_raw')
      .select(`
        id,
        careem_captain_id,
        driver_name,
        phone_number,
        total_earnings,
        cash_balance,
        report_week_start,
        report_week_end,
        processed_to_accounting,
        created_at,
        driver_id,
        drivers (
          name,
          driver_id
        )
      `)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters.dateFrom) {
      query = query.gte('report_week_start', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('report_week_end', filters.dateTo);
    }
    if (filters.processed !== undefined && filters.processed !== null) {
      query = query.eq('processed_to_accounting', filters.processed);
    }
    if (filters.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching Careem raw data:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const getUberRawData = async (filters: {
  dateFrom?: string;
  dateTo?: string;
  processed?: boolean;
  driverId?: string;
} = {}) => {
  try {
    let query = supabase
      .from('uber_income_raw')
      .select(`
        id,
        uber_driver_uuid,
        first_name,
        last_name,
        full_name,
        total_income,
        net_fare,
        report_week_start,
        report_week_end,
        processed_to_accounting,
        created_at,
        driver_id,
        drivers (
          name,
          driver_id
        )
      `)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters.dateFrom) {
      query = query.gte('report_week_start', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('report_week_end', filters.dateTo);
    }
    if (filters.processed !== undefined && filters.processed !== null) {
      query = query.eq('processed_to_accounting', filters.processed);
    }
    if (filters.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching Uber raw data:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const getSalikRawData = async (filters: {
  dateFrom?: string;
  dateTo?: string;
  processed?: boolean;
  vehicleId?: string;
  driverId?: string;
} = {}) => {
  try {
    let query = supabase
      .from('salik_tolls')
      .select(`
        id,
        transaction_date,
        license_plate,
        toll_gate_name,
        amount,
        processed_to_accounting,
        created_at,
        vehicle_id,
        driver_id,
        vehicles (
          license_plate,
          make_model
        ),
        drivers (
          name,
          driver_id
        )
      `)
      .order('transaction_date', { ascending: false });

    // Apply filters
    if (filters.dateFrom) {
      query = query.gte('transaction_date', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('transaction_date', filters.dateTo);
    }
    if (filters.processed !== undefined && filters.processed !== null) {
      query = query.eq('processed_to_accounting', filters.processed);
    }
    if (filters.vehicleId) {
      query = query.eq('vehicle_id', filters.vehicleId);
    }
    if (filters.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching Salik raw data:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error occurred' };
  }
};

export const getFinesRawData = async (filters: {
  dateFrom?: string;
  dateTo?: string;
  processed?: boolean;
  vehicleId?: string;
  driverId?: string;
} = {}) => {
  try {
    let query = supabase
      .from('traffic_fines')
      .select(`
        id,
        fine_number,
        issue_date,
        license_plate,
        total_amount,
        amount_after_discount,
        processed_to_accounting,
        created_at,
        vehicle_id,
        driver_id,
        vehicles (
          license_plate,
          make_model
        ),
        drivers (
          name,
          driver_id
        )
      `)
      .order('issue_date', { ascending: false });

    // Apply filters
    if (filters.dateFrom) {
      query = query.gte('issue_date', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('issue_date', filters.dateTo);
    }
    if (filters.processed !== undefined && filters.processed !== null) {
      query = query.eq('processed_to_accounting', filters.processed);
    }
    if (filters.vehicleId) {
      query = query.eq('vehicle_id', filters.vehicleId);
    }
    if (filters.driverId) {
      query = query.eq('driver_id', filters.driverId);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching Fines raw data:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error occurred' };
  }
};

// Summary Statistics Functions
export const getAccountingViewSummary = async (filters = {}) => {
  try {
    const [caremResult, uberResult, salikResult, finesResult] = await Promise.all([
      getCaremRawData(filters),
      getUberRawData(filters),
      getSalikRawData(filters),
      getFinesRawData(filters)
    ]);

    const summary = {
      careem: {
        total: caremResult.data?.length || 0,
        processed: caremResult.data?.filter(item => item.processed_to_accounting).length || 0,
        totalEarnings: caremResult.data?.reduce((sum, item) => sum + (item.total_earnings || 0), 0) || 0
      },
      uber: {
        total: uberResult.data?.length || 0,
        processed: uberResult.data?.filter(item => item.processed_to_accounting).length || 0,
        totalIncome: uberResult.data?.reduce((sum, item) => sum + (item.total_income || 0), 0) || 0
      },
      salik: {
        total: salikResult.data?.length || 0,
        processed: salikResult.data?.filter(item => item.processed_to_accounting).length || 0,
        totalAmount: salikResult.data?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0
      },
      fines: {
        total: finesResult.data?.length || 0,
        processed: finesResult.data?.filter(item => item.processed_to_accounting).length || 0,
        totalAmount: finesResult.data?.reduce((sum, item) => sum + (item.amount_after_discount || 0), 0) || 0
      }
    };

    return { data: summary, error: null };
  } catch (error) {
    console.error('Error fetching accounting view summary:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error occurred' };
  }
};

// ===== CSV IMPORT FUNCTIONS =====

/**
 * Parse CSV text content into array of objects
 */
function parseCSVContent(csvContent: string): any[] {
  const lines = csvContent.split(/\r?\n/);
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line) {
      // Handle CSV parsing with quoted fields
      const values = [];
      let currentValue = '';
      let insideQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim().replace(/"/g, ''));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim().replace(/"/g, ''));
      
      if (values.length >= headers.length - 1) {
        const row: any = {};
        for (let j = 0; j < headers.length; j++) {
          row[headers[j]] = values[j] || '';
        }
        data.push(row);
      }
    }
  }
  
  return data;
}

/**
 * Find or create driver by name
 */
async function findOrCreateDriver(name: string, platform: string): Promise<any> {
  try {
    // First try to find existing driver
    const { data: existingDriver, error: findError } = await supabase
      .from('drivers')
      .select('id, name')
      .eq('name', name)
      .single();
    
    if (existingDriver) {
      return existingDriver;
    }
    
    // If not found, create new driver
    const { data: newDriver, error: createError } = await supabase
      .from('drivers')
      .insert({
        name: name,
        status: 'active',
        phone: `${platform}_${Date.now()}` // Temporary placeholder
      })
      .select('id, name')
      .single();
    
    if (createError) {
      throw createError;
    }
    
    return newDriver;
  } catch (error) {
    console.error('Error finding/creating driver:', error);
    return null;
  }
}

/**
 * Import Careem payment data from CSV
 */
export async function importCareemPaymentData(csvContent: string) {
  try {
    const data = parseCSVContent(csvContent);
    let processedCount = 0;
    let errorCount = 0;
    const batchId = `careem_${Date.now()}`;
    
    for (const row of data) {
      try {
        const captainName = row['Captain Name'] || row['captain_name'] || 'Unknown Captain';
        const earnings = parseFloat(row['Total Earnings'] || row['total_earnings'] || '0');
        const trips = parseInt(row['Total Trips'] || row['total_trips'] || '0');
        
        if (earnings > 0) {
          const driver = await findOrCreateDriver(captainName, 'careem');
          
          if (driver) {
            const { error: transactionError } = await supabase
              .from('accounting_transactions')
              .insert({
                driver_id: driver.id,
                type: 'earning',
                category: 'careem_payment',
                amount: earnings,
                description: `Careem earnings: ${trips} trips`,
                transaction_date: new Date().toISOString().split('T')[0],
                status: 'completed',
                batch_reference: batchId
              });
            
            if (transactionError) {
              throw transactionError;
            }
            
            processedCount++;
          }
        }
      } catch (error) {
        errorCount++;
        console.error('Error processing Careem row:', error);
      }
    }
    
    return {
      success: true,
      count: processedCount,
      errors: errorCount,
      batchId: batchId,
      message: `Successfully imported ${processedCount} Careem payment records`
    };
  } catch (error) {
    return {
      success: false,
      count: 0,
      errors: 1,
      message: error instanceof Error ? error.message : 'Failed to import Careem data'
    };
  }
}

/**
 * Import Uber captain data from CSV
 */
export async function importUberCaptainData(csvContent: string) {
  try {
    const data = parseCSVContent(csvContent);
    let processedCount = 0;
    let errorCount = 0;
    const batchId = `uber_${Date.now()}`;
    
    for (const row of data) {
      try {
        // Handle both Arabic and English column names
        const firstName = row['الاسم الأول للشريك السائق'] || row['First Name'] || '';
        const lastName = row['اسم العائلة للشريك السائق'] || row['Last Name'] || '';
        const totalIncome = parseFloat(row['إجمالي الدخل'] || row['Total Income'] || '0');
        
        const fullName = `${firstName} ${lastName}`.trim();
        
        if (totalIncome > 0 && fullName !== '') {
          const driver = await findOrCreateDriver(fullName, 'uber');
          
          if (driver) {
            const { error: transactionError } = await supabase
              .from('accounting_transactions')
              .insert({
                driver_id: driver.id,
                type: 'earning',
                category: 'uber_payment',
                amount: totalIncome,
                description: `Uber earnings`,
                transaction_date: new Date().toISOString().split('T')[0],
                status: 'completed',
                batch_reference: batchId
              });
            
            if (transactionError) {
              throw transactionError;
            }
            
            processedCount++;
          }
        }
      } catch (error) {
        errorCount++;
        console.error('Error processing Uber row:', error);
      }
    }
    
    return {
      success: true,
      count: processedCount,
      errors: errorCount,
      batchId: batchId,
      message: `Successfully imported ${processedCount} Uber payment records`
    };
  } catch (error) {
    return {
      success: false,
      count: 0,
      errors: 1,
      message: error instanceof Error ? error.message : 'Failed to import Uber data'
    };
  }
}

/**
 * Import traffic fines data from CSV
 */
export async function importTrafficFinesData(csvContent: string) {
  try {
    const data = parseCSVContent(csvContent);
    let processedCount = 0;
    let errorCount = 0;
    const batchId = `fines_${Date.now()}`;
    
    // First get vehicle mappings
    const { data: vehicles } = await supabase
      .from('vehicles')
      .select('id, license_plate');
    
    const vehicleMap = new Map();
    vehicles?.forEach(v => {
      vehicleMap.set(v.license_plate, v.id);
    });
    
    for (const row of data) {
      try {
        const plateNumber = row['License Plate'] || row['Plate Number'] || row['plate_number'] || '';
        const amount = parseFloat(row['Amount'] || row['Fine Amount'] || row['amount'] || '0');
        const description = row['Description'] || row['Violation'] || 'Traffic Fine';
        
        if (amount > 0 && plateNumber) {
          const vehicleId = vehicleMap.get(plateNumber);
          
          if (vehicleId) {
            const { error: transactionError } = await supabase
              .from('accounting_transactions')
              .insert({
                vehicle_id: vehicleId,
                type: 'expense',
                category: 'traffic_fine',
                amount: amount,
                description: description,
                transaction_date: new Date().toISOString().split('T')[0],
                status: 'pending',
                batch_reference: batchId
              });
            
            if (transactionError) {
              throw transactionError;
            }
            
            processedCount++;
          }
        }
      } catch (error) {
        errorCount++;
        console.error('Error processing traffic fine row:', error);
      }
    }
    
    return {
      success: true,
      count: processedCount,
      errors: errorCount,
      batchId: batchId,
      message: `Successfully imported ${processedCount} traffic fine records`
    };
  } catch (error) {
    return {
      success: false,
      count: 0,
      errors: 1,
      message: error instanceof Error ? error.message : 'Failed to import traffic fines data'
    };
  }
}

/**
 * Process Salik CSV data
 */
export async function processSalikCSV(csvContent: string) {
  try {
    const data = parseCSVContent(csvContent);
    let processedCount = 0;
    let errorCount = 0;
    const batchId = `salik_${Date.now()}`;
    
    // First get vehicle mappings
    const { data: vehicles } = await supabase
      .from('vehicles')
      .select('id, license_plate');
    
    const vehicleMap = new Map();
    vehicles?.forEach(v => {
      vehicleMap.set(v.license_plate, v.id);
    });
    
    for (const row of data) {
      try {
        const plateNumber = row['Plate Number'] || row['License Plate'] || row['plate_number'] || '';
        const amount = parseFloat(row['Amount'] || row['Toll Amount'] || row['amount'] || '0');
        const location = row['Location'] || row['Toll Gate'] || row['Gate'] || 'Salik Toll';
        
        if (amount > 0 && plateNumber) {
          const vehicleId = vehicleMap.get(plateNumber);
          
          if (vehicleId) {
            const { error: transactionError } = await supabase
              .from('accounting_transactions')
              .insert({
                vehicle_id: vehicleId,
                type: 'expense',
                category: 'salik_toll',
                amount: amount,
                description: `Salik toll - ${location}`,
                transaction_date: new Date().toISOString().split('T')[0],
                status: 'completed',
                batch_reference: batchId
              });
            
            if (transactionError) {
              throw transactionError;
            }
            
            processedCount++;
          }
        }
      } catch (error) {
        errorCount++;
        console.error('Error processing Salik row:', error);
      }
    }
    
    return {
      success: true,
      count: processedCount,
      errors: errorCount,
      batchId: batchId,
      message: `Successfully imported ${processedCount} Salik toll records`
    };
  } catch (error) {
    return {
      success: false,
      count: 0,
      errors: 1,
      message: error instanceof Error ? error.message : 'Failed to import Salik data'
    };
  }
}

/**
 * Import credit terms data
 */
export async function importCreditTermsData(csvContent: string, termType: string = 'A') {
  try {
    const data = parseCSVContent(csvContent);
    let processedCount = 0;
    let errorCount = 0;
    const batchId = `credit_${termType}_${Date.now()}`;
    
    for (const row of data) {
      try {
        const driverName = row['Driver Name'] || row['Name'] || '';
        const amount = parseFloat(row['Amount'] || row['Credit Amount'] || '0');
        
        if (amount > 0 && driverName) {
          const driver = await findOrCreateDriver(driverName, 'credit');
          
          if (driver) {
            const { error: transactionError } = await supabase
              .from('accounting_transactions')
              .insert({
                driver_id: driver.id,
                type: 'earning',
                category: `credit_terms_${termType.toLowerCase()}`,
                amount: amount,
                description: `Credit Terms ${termType}`,
                transaction_date: new Date().toISOString().split('T')[0],
                status: 'completed',
                batch_reference: batchId
              });
            
            if (transactionError) {
              throw transactionError;
            }
            
            processedCount++;
          }
        }
      } catch (error) {
        errorCount++;
        console.error('Error processing credit terms row:', error);
      }
    }
    
    return {
      success: true,
      count: processedCount,
      errors: errorCount,
      batchId: batchId,
      message: `Successfully imported ${processedCount} credit terms ${termType} records`
    };
  } catch (error) {
    return {
      success: false,
      count: 0,
      errors: 1,
      message: error instanceof Error ? error.message : 'Failed to import credit terms data'
    };
  }
}

/**
 * Get import batch summary
 */
export async function getImportBatchSummary(batchId: string) {
  try {
    const { data, error } = await supabase
      .from('accounting_transactions')
      .select('*')
      .eq('batch_reference', batchId);
    
    if (error) throw error;
    
    const summary = {
      totalRecords: data.length,
      totalAmount: data.reduce((sum, t) => sum + t.amount, 0),
      categories: data.reduce((cats, t) => {
        cats[t.category] = (cats[t.category] || 0) + 1;
        return cats;
      }, {} as Record<string, number>)
    };
    
    return summary;
  } catch (error) {
    console.error('Error getting batch summary:', error);
    return null;
  }
}

// Bulk fix function to sync all vehicle assignment statuses
export const fixAllVehicleAssignmentStatuses = async () => {
  console.log('🔧 Starting bulk vehicle assignment status fix...');
  
  try {
    // 1. Get all vehicles with mismatched status
    const { data: vehicles, error: vehicleError } = await supabase
      .from('vehicles')
      .select('id, license_plate, car_status, current_driver_id');
    
    if (vehicleError) {
      console.error('❌ Error fetching vehicles:', vehicleError);
      return;
    }
    
    // 2. Get all active assignments
    const { data: activeAssignments, error: assignmentError } = await supabase
      .from('assignment_history')
      .select('vehicle_id, primary_driver_id, secondary_driver_id, is_shared, assignment_date')
      .eq('status', 'active');
    
    if (assignmentError) {
      console.error('❌ Error fetching assignments:', assignmentError);
      return;
    }
    
    console.log(`📊 Found ${vehicles.length} vehicles and ${activeAssignments.length} active assignments`);
    
    // 3. Create a map of vehicles that should be "work" status
    const vehiclesToFix = [];
    const assignmentMap = new Map();
    
    activeAssignments.forEach(assignment => {
      assignmentMap.set(assignment.vehicle_id, assignment);
    });
    
    // 4. Find vehicles that need status correction
    vehicles.forEach(vehicle => {
      const hasActiveAssignment = assignmentMap.has(vehicle.id);
      const shouldBeWork = hasActiveAssignment;
      const isWork = vehicle.car_status === 'work';
      
      if (shouldBeWork && !isWork) {
        const assignment = assignmentMap.get(vehicle.id);
        vehiclesToFix.push({
          vehicle,
          assignment,
          currentStatus: vehicle.car_status,
          shouldBe: 'work'
        });
      } else if (!shouldBeWork && isWork && !vehicle.current_driver_id) {
        vehiclesToFix.push({
          vehicle,
          assignment: null,
          currentStatus: vehicle.car_status,
          shouldBe: 'available'
        });
      }
    });
    
    console.log(`🔍 Found ${vehiclesToFix.length} vehicles with status mismatches:`);
    vehiclesToFix.forEach(item => {
      console.log(`  - ${item.vehicle.license_plate}: ${item.currentStatus} → ${item.shouldBe}`);
    });
    
    // 5. Fix each vehicle
    const fixResults = [];
    for (const item of vehiclesToFix) {
      try {
        const updateData = {
          car_status: item.shouldBe,
          current_driver_id: item.assignment?.primary_driver_id || null,
          updated_at: new Date().toISOString()
        };
        
        const { error: updateError } = await supabase
          .from('vehicles')
          .update(updateData)
          .eq('id', item.vehicle.id);
          
        if (updateError) {
          console.error(`❌ Failed to update ${item.vehicle.license_plate}:`, updateError);
          fixResults.push({ vehicle: item.vehicle.license_plate, success: false, error: updateError });
        } else {
          console.log(`✅ Fixed ${item.vehicle.license_plate}: ${item.currentStatus} → ${item.shouldBe}`);
          fixResults.push({ vehicle: item.vehicle.license_plate, success: true });
        }
      } catch (error) {
        console.error(`❌ Error updating ${item.vehicle.license_plate}:`, error);
        fixResults.push({ vehicle: item.vehicle.license_plate, success: false, error });
      }
    }
    
    // 6. Summary
    const successCount = fixResults.filter(r => r.success).length;
    const failCount = fixResults.filter(r => !r.success).length;
    
    console.log(`🎉 Bulk fix completed:`);
    console.log(`  ✅ Successfully fixed: ${successCount} vehicles`);
    console.log(`  ❌ Failed to fix: ${failCount} vehicles`);
    
    if (successCount > 0) {
      console.log('🔄 Refreshing vehicle data in stores...');
      // Trigger a refresh of vehicle data in the app
      const event = new CustomEvent('vehicleStatusUpdated');
      window.dispatchEvent(event);
    }
    
    return { 
      total: vehiclesToFix.length, 
      fixed: successCount, 
      failed: failCount,
      results: fixResults 
    };
    
  } catch (error) {
    console.error('❌ Error in bulk fix:', error);
    return null;
  }
};

// Debug function to check driver-vehicle assignment sync
export const debugDriverVehicleSync = async (licensePlate: string, driverName?: string) => {
  console.log(`🔍 Debugging driver-vehicle sync for: Vehicle ${licensePlate}${driverName ? ` and Driver ${driverName}` : ''}`);
  
  try {
    // 1. Get vehicle info
    const { data: vehicle, error: vehicleError } = await supabase
      .from('vehicles')
      .select('*')
      .eq('license_plate', licensePlate)
      .single();
    
    if (vehicleError) {
      console.error('❌ Error fetching vehicle:', vehicleError);
      return;
    }
    
    console.log('🚗 Vehicle info:', {
      id: vehicle.id,
      license_plate: vehicle.license_plate,
      car_status: vehicle.car_status,
      current_driver_id: vehicle.current_driver_id
    });
    
    // 2. Get driver info if vehicle has assigned driver
    let assignedDriver = null;
    if (vehicle.current_driver_id) {
      const { data: driverData, error: driverError } = await supabase
        .from('drivers')
        .select('*')
        .eq('id', vehicle.current_driver_id)
        .single();
        
      if (!driverError && driverData) {
        assignedDriver = driverData;
        console.log('👤 Vehicle assigned to driver:', {
          id: assignedDriver.id,
          name: assignedDriver.name,
          phone: assignedDriver.phone,
          current_vehicle_id: assignedDriver.assigned_vehicle_id
        });
      }
    }
    
    // 3. If specific driver name provided, check that driver too
    let specificDriver = null;
    if (driverName) {
      const { data: drivers, error: driverSearchError } = await supabase
        .from('drivers')
        .select('*')
        .ilike('name', `%${driverName}%`);
        
      if (!driverSearchError && drivers && drivers.length > 0) {
        specificDriver = drivers[0];
        console.log('👤 Specific driver info:', {
          id: specificDriver.id,
          name: specificDriver.name,
          phone: specificDriver.phone,
          current_vehicle_id: specificDriver.assigned_vehicle_id
        });
      }
    }
    
    // 4. Check active assignments
    const { data: assignments, error: assignmentError } = await supabase
      .from('assignment_history')
      .select('*')
      .eq('vehicle_id', vehicle.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    
    if (!assignmentError && assignments && assignments.length > 0) {
      console.log('📋 Active assignment found:', {
        id: assignments[0].id,
        primary_driver_id: assignments[0].primary_driver_id,
        assignment_date: assignments[0].assignment_date
      });
      
      // Check if assignment matches current vehicle driver
      if (assignments[0].primary_driver_id !== vehicle.current_driver_id) {
        console.warn('⚠️ MISMATCH: Active assignment driver differs from vehicle current driver');
        console.log('🔧 Fixing vehicle current_driver_id to match assignment...');
        
        const { error: updateError } = await supabase
          .from('vehicles')
          .update({
            current_driver_id: assignments[0].primary_driver_id,
            updated_at: new Date().toISOString()
          })
          .eq('id', vehicle.id);
          
        if (!updateError) {
          console.log('✅ Vehicle current_driver_id updated to match assignment');
        }
      }
    } else {
      console.log('❌ No active assignments found for this vehicle');
    }
    
    return { vehicle, assignedDriver, specificDriver, assignments };
    
  } catch (error) {
    console.error('❌ Error debugging sync:', error);
    return null;
  }
};

// Debug function to check and fix vehicle assignment status
export const debugVehicleAssignmentStatus = async (licensePlate: string) => {
  console.log(`🔍 Debugging vehicle assignment status for: ${licensePlate}`);
  
  try {
    // 1. Get vehicle info
    const { data: vehicle, error: vehicleError } = await supabase
      .from('vehicles')
      .select('*')
      .eq('license_plate', licensePlate)
      .single();
    
    if (vehicleError) {
      console.error('❌ Error fetching vehicle:', vehicleError);
      return;
    }
    
    console.log('🚗 Vehicle info:', {
      id: vehicle.id,
      license_plate: vehicle.license_plate,
      car_status: vehicle.car_status,
      current_driver_id: vehicle.current_driver_id,
      assignment_date: vehicle.assignment_date
    });
    
    // 2. Check for active assignments
    const { data: assignments, error: assignmentError } = await supabase
      .from('assignment_history')
      .select('*')
      .eq('vehicle_id', vehicle.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    
    if (assignmentError) {
      console.error('❌ Error fetching assignments:', assignmentError);
      return;
    }
    
    console.log('📋 Active assignments found:', assignments?.length || 0);
    if (assignments && assignments.length > 0) {
      console.log('📋 Most recent assignment:', {
        id: assignments[0].id,
        primary_driver_id: assignments[0].primary_driver_id,
        assignment_date: assignments[0].assignment_date,
        status: assignments[0].status,
        is_shared: assignments[0].is_shared
      });
      
      // 3. Get driver info if assignment exists
      if (assignments[0].primary_driver_id) {
        const { data: driver, error: driverError } = await supabase
          .from('drivers')
          .select('id, name, phone, assigned_vehicle_id, car_plate_number')
          .eq('id', assignments[0].primary_driver_id)
          .single();
          
        if (!driverError && driver) {
          console.log('👤 Assigned driver:', {
            id: driver.id,
            name: driver.name,
            phone: driver.phone,
            current_vehicle_id: driver.assigned_vehicle_id,
            car_plate_number: driver.car_plate_number
          });
        }
      }
      
      // 4. Check if vehicle status needs to be corrected
      if (vehicle.car_status === 'available' && assignments.length > 0) {
        console.log('🔧 Vehicle status mismatch detected! Vehicle is "available" but has active assignment.');
        console.log('🔧 Fixing vehicle status...');
        
        const { error: updateError } = await supabase
          .from('vehicles')
          .update({
            car_status: 'work',
            current_driver_id: assignments[0].primary_driver_id,
            updated_at: new Date().toISOString()
          })
          .eq('id', vehicle.id);
          
        if (updateError) {
          console.error('❌ Failed to update vehicle status:', updateError);
        } else {
          console.log('✅ Vehicle status updated to "work"');
        }
      }
    } else {
      console.log('⚠️ No active assignments found for this vehicle');
      if (vehicle.car_status === 'work') {
        console.log('🔧 Vehicle shows as "work" but no active assignment. Consider updating to "available".');
      }
    }
    
    return {
      vehicle,
      assignments,
      needsStatusFix: vehicle.car_status === 'available' && assignments && assignments.length > 0
    };
    
  } catch (error) {
    console.error('❌ Error debugging vehicle status:', error);
    return null;
  }
}

/**
 * Upload driver photo to Supabase Storage
 * @param file - File blob or URI
 * @param filename - Name for the file
 * @returns Object with publicUrl
 */
export const uploadDriverPhoto = async (file: Blob | File, filename: string) => {
  try {
    console.log('📸 Uploading driver photo:', filename);

    // Upload to driver-images bucket
    const { data, error } = await supabase.storage
      .from('driver-images')
      .upload(filename, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      console.error('❌ Photo upload error:', error);
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('driver-images')
      .getPublicUrl(filename);

    console.log('✅ Photo uploaded successfully:', urlData.publicUrl);
    return { publicUrl: urlData.publicUrl };
  } catch (error) {
    console.error('❌ Failed to upload driver photo:', error);
    throw error;
  }
};

/**
 * Delete driver photo from Supabase Storage
 * @param photoUrl - Full URL of the photo to delete
 */
export const deleteDriverPhoto = async (photoUrl: string) => {
  try {
    if (!photoUrl || !photoUrl.includes('driver-images')) {
      return;
    }

    // Extract filename from URL
    const filename = photoUrl.split('/driver-images/').pop();
    if (!filename) return;

    console.log('🗑️ Deleting driver photo:', filename);

    const { error } = await supabase.storage
      .from('driver-images')
      .remove([filename]);

    if (error) {
      console.error('❌ Photo deletion error:', error);
      throw error;
    }

    console.log('✅ Photo deleted successfully');
  } catch (error) {
    console.error('❌ Failed to delete driver photo:', error);
    // Don't throw - deletion errors shouldn't block other operations
  }
};;


